import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Users, AlertCircle, CheckCircle, Eye, EyeOff, Building2, CreditCard,
    MapPin, TreePine, FileText, X, Save, Camera, Loader2, Upload,
    Navigation, Play, Pause, Video, Edit, Plus, ChevronDown, ChevronUp,
    Menu, ArrowRight, Home, Factory, Waves, Droplets, Zap, Package, Fence,
    Trash2, RefreshCw
} from "lucide-react";
import apiService from "../../config";
import { useLanguage } from "../../StoreContext/LanguageContext";

// ===================================================================
// CONSTANTS AND UTILITY FUNCTIONS
// ===================================================================


/**
 * Detects file type from URL extension for document preview
 */
const getFileKind = (url) => {
    if (!url) return { type: 'unknown', isImage: false, isPdf: false, isVideo: false };

    const ext = url.toLowerCase().split('.').pop();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    const isPdf = ext === 'pdf';
    const isVideo = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'm4v'].includes(ext);

    return {
        type: isImage ? 'image' : isPdf ? 'pdf' : isVideo ? 'video' : 'unknown',
        isImage,
        isPdf,
        isVideo
    };
};

/**
 * Normalizes property ID field name (handles both property_id and Property_id)
 */
const getPropertyId = (item) => {
    return item.property_id || item.Property_id || null;
};

// ===================================================================
// SMALL REUSABLE COMPONENTS
// ===================================================================

const SectionCard = ({ children, className = "" }) => (
    <div className={`rounded-xl border border-gray-200 bg-white p-3 md:p-4 ${className}`}>
        {children}
    </div>
);

const SmallButton = ({ variant = "primary", size = "sm", children, className = "", ...props }) => {
    const baseClasses = "h-8 px-3 text-sm rounded-lg font-medium transition-colors flex items-center gap-2";
    const variants = {
        primary: "bg-blue-500 hover:bg-blue-700 text-white disabled:bg-gray-400",
        secondary: "bg-blue-300 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100",
        success: "bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400",
        danger: "bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-400",
        warning: "bg-yellow-600 hover:bg-yellow-700 text-white disabled:bg-gray-400"
    };

    return (
        <button className={`${baseClasses} ${variants[variant]} ${className}`} {...props}>
            {children}
        </button>
    );
};

const Field = ({ label, children, error }) => (
    <div className="space-y-1">
        {label && <label className="block text-xs font-medium text-gray-700">{label}</label>}
        {children}
        {error && (
            <div className="flex items-center text-red-500 text-xs">
                <AlertCircle className="h-3 w-3 mr-1" />
                {error}
            </div>
        )}
    </div>
);

const Row = ({ label, value, className = "" }) => (
    <div className={`py-2 flex justify-between items-center border-b border-gray-100 last:border-0 ${className}`}>
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-sm font-medium">{value}</span>
    </div>
);

// ===================================================================
// DOCUMENT PREVIEW MODAL COMPONENT
// ===================================================================

const DocumentPreviewModal = ({ document, isOpen, onClose }) => {
    if (!isOpen || !document) return null;

    const fileInfo = getFileKind(document);

    const openInNewTab = () => {
        window.open(document, "_blank");
    };

    const handleError = () => {
        console.error("Failed to load document:", document);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-2">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-3 border-b">
                    <h3 className="text-sm font-medium">Document Preview</h3>
                    <div className="flex items-center gap-2">
                        <SmallButton variant="secondary" onClick={openInNewTab}>
                            <Eye className="h-4 w-4" />
                            Open in New Tab
                        </SmallButton>
                        <button
                            onClick={onClose}
                            className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center">
                    {fileInfo.isImage && (
                        <img
                            src={document}
                            alt="Document"
                            className="max-w-full max-h-full object-contain"
                            onError={handleError}
                        />
                    )}
                    {fileInfo.isPdf && (
                        <iframe
                            src={document}
                            className="w-full h-full border-0"
                            title="PDF Document"
                            onError={handleError}
                        />
                    )}
                    {fileInfo.isVideo && (
                        <video
                            src={document}
                            controls
                            className="max-w-full max-h-full"
                            onError={handleError}
                        />
                    )}
                    {!fileInfo.isImage && !fileInfo.isPdf && !fileInfo.isVideo && (
                        <div className="text-center p-4">
                            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 mb-2">Cannot preview this file type</p>
                            <SmallButton variant="primary" onClick={openInNewTab}>
                                <Eye className="h-4 w-4" />
                                Open in New Tab
                            </SmallButton>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ===================================================================
// MAIN COMPONENT
// ===================================================================

const HighwayEditOwner = ({
    owner, onBack,
    editFormData,
    setEditFormData,
    onAdd,
    onCancel,
    onRefresh,
    landAcquisition,
    isOpen = true,
    onClose,
    prefillData,
}) => {

    // ===================================================================
    // STATE MANAGEMENT
    // ===================================================================

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [saving, setSaving] = useState(false);
    const { language, changeLanguage, translations } = useLanguage();
    const [showLanguage, setShowLanguage] = useState(false);
    const getCurrentLanguageCode = () => {
        for (const [uiCode, contextCode] of Object.entries(languageCodeMap)) {
            if (contextCode === language) {
                return uiCode;
            }
        }
        return "EN"; // Default
    };
    const languageCodeMap = {
        EN: "en",
        HI: "hi",
        MR: "mr",
        ES: "es",
    };
    const handleLanguageChange = (code) => {
        const contextLangCode = languageCodeMap[code] || "en";
        changeLanguage(contextLangCode);
        setShowLanguage(false);
    };
    const STEP_DEFINITIONS = {
        1: { title: `${translations[language].Joverview}`, icon: MapPin, color: "orange", description: "Property details and summary" },
        2: { title: `${translations[language].Jtree}`, icon: TreePine, color: "green", description: "Tree inventory management" },
        3: { title: `${translations[language].Jhouse}`, icon: Home, color: "indigo", description: "Residential structures" },
        4: { title: `${translations[language].Jwire}`, icon: Fence, color: "purple", description: "Boundary fencing" },
        5: { title: `${translations[language].Jfarmpond}`, icon: Waves, color: "blue", description: "Water reservoirs" },
        6: { title: `${translations[language].Jwells}`, icon: Droplets, color: "cyan", description: "Water wells" },
        7: { title: `${translations[language].Jborewells}`, icon: Zap, color: "blue", description: "Drilled wells" },
        8: { title: `${translations[language].Jpipeline}`, icon: Zap, color: "sky", description: "Water pipes" },
        9: { title: `${translations[language].Jothers}`, icon: Package, color: "gray", description: "Miscellaneous assets" },
        10: { title: `${translations[language].Jlocation}`, icon: Navigation, color: "indigo", description: "GPS and media" },
        11: { title: `${translations[language].Jremark}`, icon: FileText, color: "yellow", description: "Additional remarks and justification" },
    };

    // Error and validation states
    const [error, setError] = useState("");
    const [validationErrors, setValidationErrors] = useState({});

    // UI states
    const [currentStep, setCurrentStep] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [completedSections, setCompletedSections] = useState({});

    // Form visibility states
    const [showAadhaar, setShowAadhaar] = useState(false);
    const [showAccount, setShowAccount] = useState(false);

    const [editingAssetId, setEditingAssetId] = useState(null);
    const [editingAssetType, setEditingAssetType] = useState(null);

    // Camera and media states
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [currentAssetType, setCurrentAssetType] = useState(null);
    const [currentAssetIndex, setCurrentAssetIndex] = useState(null);
    const [currentAssetData, setCurrentAssetData] = useState(null);
    const [capturedPhotos, setCapturedPhotos] = useState({});
    const [existingPhotos, setExistingPhotos] = useState({});
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    // Tree management states
    const [treeTypes, setTreeTypes] = useState([]);
    const [treeCategories, setTreeCategories] = useState([]);
    const [editingTreeId, setEditingTreeId] = useState(null);

    // Modal states
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ title: "", message: "", type: "info" });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({});

    // Document preview modal
    const [documentPreview, setDocumentPreview] = useState({ isOpen: false, document: null });

    const [isCameraActive, setIsCameraActive] = useState(false);
    const [assetWithCameraOpen, setAssetWithCameraOpen] = useState(null);
    // Section collapse states
    const [collapsedSections, setCollapsedSections] = useState({
        trees: false, homes: false, wireFencing: false,
        farmPonds: false, wells: false, borewells: false, pipelines: false, others: false
    });

    // Additional states
    const [ownersData, setOwnersData] = useState([]);
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [landAcquisition_state, setLandAcquisition_state] = useState(null);
    const [owners, setOwners] = useState([]);

    // Refs
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const cameraInputRef = useRef(null);

    // Container refs for different asset types
    const homesContainerRef = useRef(null);
    const wireFenceContainerRef = useRef(null);
    const farmPondContainerRef = useRef(null);
    const treeContainerRef = useRef(null);
    const wellContainerRef = useRef(null);
    const borewellContainerRef = useRef(null);
    const pipelineContainerRef = useRef(null);
    const otherContainerRef = useRef(null);

    // ===================================================================
    // CORE UTILITY FUNCTIONS
    // ===================================================================

    /**
     * Shows alert notifications with auto-dismiss
     */
    const showAlert = useCallback((title, message, type = "info") => {
        setAlertConfig({ title, message, type });
        setShowAlertModal(true);
        if (type !== "success") {
            setTimeout(() => setShowAlertModal(false), 5000);
        }
    }, []);

    /**
     * Shows confirmation dialog
     */
    const showConfirm = useCallback((title, message, onConfirm, onCancel = null, type = "warning") => {
        setConfirmConfig({ title, message, onConfirm, onCancel, type });
        setShowConfirmModal(true);
    }, []);

    /**
     * Validates form fields with context-aware rules
     */
    const validateField = useCallback((fieldName, value) => {
        switch (fieldName) {
            case "actual_occupant":
                if (!value || value.trim().length < 2) {
                    return "Occupant name must be at least 2 characters long";
                }
                break;

            case "aadhaar_number":
                if (["Individual", "Joint"].includes(editFormData.owner_type)) {
                    if (!value) return "Aadhaar number is required";
                    if (!/^\d{12}$/.test(value)) return "Aadhaar number must be exactly 12 digits";
                } else if (value && !/^\d{12}$/.test(value)) {
                    return "Aadhaar number must be exactly 12 digits";
                }
                break;

            case "pan_number":
                if (["Individual", "Joint"].includes(editFormData.owner_type)) {
                    if (!value) return "PAN number is required";
                    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) return "Invalid PAN number format";
                } else if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
                    return "Invalid PAN number format";
                }
                break;

            case "latitude":
                if (value && (isNaN(value) || value < -90 || value > 90)) {
                    return "Latitude must be between -90 and 90 degrees";
                }
                break;

            case "longitude":
                if (value && (isNaN(value) || value < -180 || value > 180)) {
                    return "Longitude must be between -180 and 180 degrees";
                }
                break;

            default:
                break;
        }
        return "";
    }, [editFormData.owner_type]);

    /**
     * Updates form field values with validation
     */
    const handleFieldChange = useCallback((fieldName, value) => {
        console.log(`Updating field: ${fieldName} =`, value);
        setEditFormData(prev => ({ ...prev, [fieldName]: value }));
        const error = validateField(fieldName, value);
        setValidationErrors(prev => ({ ...prev, [fieldName]: error }));
    }, [setEditFormData, validateField]);

    const hasStepData = (stepNum) => {
        switch (stepNum) {
            case 1: return editFormData.actual_occupant || editFormData.owner_address || editFormData.aadhaar_number || editFormData.pan_number || editFormData.account_number || editFormData.ifsc_code;
            case 2: return editFormData.tree_entries?.length > 0;
            case 3: return editFormData.home_entries?.length > 0;
            case 4: return editFormData.wirefence_entries?.length > 0;
            case 5: return editFormData.farmpond_entries?.length > 0;
            case 6: return editFormData.well_entries?.length > 0;
            case 7: return editFormData.borewell_entries?.length > 0;
            case 8: return editFormData.pipeline_entries?.length > 0;
            case 9: return editFormData.other_entries?.length > 0;
            case 10: return editFormData.media_video || editFormData.media_image || selectedVideo || selectedImage;
            case 11: return editFormData.remarks || editFormData.justification;
            default: return false;
        }
    };
    // ===================================================================
    // DOCUMENT HANDLING FUNCTIONS
    // ===================================================================

    /**
     * Handles file upload with validation
     */
    const handleFileUpload = useCallback((assetType, index, file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            showAlert('Error', 'Only images (JPG, PNG, GIF) and PDF files are allowed', 'error');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            showAlert('Error', 'File size must be less than 10MB', 'error');
            return;
        }

        const fileUrl = URL.createObjectURL(file);

        // Update the appropriate asset type with document
        const assetKey = `${assetType}_entries`;
        if (editFormData[assetKey]) {
            const updatedAssets = [...editFormData[assetKey]];
            updatedAssets[index] = {
                ...updatedAssets[index],
                document: fileUrl,
                documentFile: file,
                saved: false
            };
            handleFieldChange(assetKey, updatedAssets);
            showAlert('Success', 'Document uploaded successfully', 'success');
        }
    }, [editFormData, handleFieldChange, showAlert]);

    /**
     * Handles viewing documents from API responses
     */
    const handleViewDocument = useCallback((document) => {
        if (!document) {
            showAlert("Warning", "No document available", "warning");
            return;
        }

        try {
            // Test if URL is accessible
            const testImg = new Image();
            testImg.onload = () => {
                setDocumentPreview({ isOpen: true, document });
            };
            testImg.onerror = () => {
                showAlert("Error", "Document not available or failed to load", "error");
            };

            // For non-images, open directly
            const fileInfo = getFileKind(document);
            if (!fileInfo.isImage) {
                setDocumentPreview({ isOpen: true, document });
            } else {
                testImg.src = document;
            }
        } catch (error) {
            showAlert("Error", "Failed to open document", "error");
        }
    }, [showAlert]);

    // ===================================================================
    // TREE MANAGEMENT FUNCTIONS
    // ===================================================================

    /**
     * Gets unique tree categories from available tree types
     */
    const getUniqueCategories = useCallback(() => {
        return [...new Set(treeTypes.map(type => type.tree_type_name1))].filter(Boolean);
    }, [treeTypes]);

    /**
     * Filters trees by category
     */
    const getTreesByCategory = useCallback((category) => {
        if (!category) return [];
        return treeTypes.filter(type => type.tree_type_name1 === category);
    }, [treeTypes]);

    /**
     * Adds new tree entry with proper initialization
     */
    const addTreeEntry = useCallback(() => {
        const currentEntries = editFormData.tree_entries || [];
        const newEntry = {
            count: "",
            category: "",
            type_id: "",
            other_type: "",
            tree_value_valuer: "",
            tree_value_lao: "",
            saved: false,
            document: null
        };
        const newEntries = [...currentEntries, newEntry];
        handleFieldChange("tree_entries", newEntries);
    }, [editFormData.tree_entries, handleFieldChange]);

    /**
     * Updates tree entry with proper validation
     */
    const updateTreeEntry = useCallback((index, field, value) => {
        const updatedEntries = [...(editFormData.tree_entries || [])];

        if (field === 'category') {
            updatedEntries[index] = {
                ...updatedEntries[index],
                category: value,
                type_id: "",
                other_type: "",
                saved: false
            };
        } else if (field === 'type_id') {
            updatedEntries[index] = {
                ...updatedEntries[index],
                type_id: value,
                other_type: value === "other" ? updatedEntries[index].other_type : "",
                saved: false
            };
        } else {
            updatedEntries[index] = {
                ...updatedEntries[index],
                [field]: value,
                saved: false
            };
        }

        handleFieldChange("tree_entries", updatedEntries);
        updateTreeFieldFormat(updatedEntries);
    }, [editFormData.tree_entries, handleFieldChange]);

    const refreshAssets = async (assetKey, ownerId) => {
        switch (assetKey) {
            case "wirefence_entries": return loadWireFencingFromAPI(ownerId);
            case "farmpond_entries": return loadFarmPondsFromAPI(ownerId);
            case "well_entries": return loadWellsFromAPI(ownerId);
            case "borewell_entries": return loadBorewellsFromAPI(ownerId);
            case "pipeline_entries": return loadPipelinesFromAPI(ownerId);
            case "other_entries": return loadOtherAssetsFromAPI(ownerId);
            default: return;
        }
    };

    /**
     * Saves tree entry to API with validation
     */
    const saveTree = useCallback(async (entry, index) => {
        // Enhanced validation
        if (!entry.count || entry.count.trim() === "" || parseInt(entry.count) <= 0) {
            showAlert("Validation Error", "Please enter a valid tree count", "error");
            return;
        }

        if (!entry.category || entry.category.trim() === "") {
            showAlert("Validation Error", "Please select a tree category", "error");
            return;
        }

        if (!entry.type_id || entry.type_id.trim() === "") {
            showAlert("Validation Error", "Please select a tree type", "error");
            return;
        }

        if (entry.type_id === "other" && (!entry.other_type || entry.other_type.trim() === "")) {
            showAlert("Validation Error", "Please enter custom tree type name", "error");
            return;
        }

        try {
            setSaving(true);

            let treeTypeName = "";
            if (entry.type_id === "other") {
                treeTypeName = entry.other_type.trim();
            } else {
                const treeType = treeTypes.find(
                    (type) => type.tree_type_id === parseInt(entry.type_id)
                );
                treeTypeName = treeType ? treeType.tree_type_name : "";
            }

            const formData = new FormData();
            formData.append("land_owner_id", editFormData.land_owner_id);
            formData.append("tree_type_name", treeTypeName);
            formData.append("tree_count", parseInt(entry.count));
            formData.append("tree_value_valuer", entry.tree_value_valuer || "");
            formData.append("tree_value_lao", entry.tree_value_lao || "");

            // Add document if available
            if (entry.documentFile) {
                formData.append("document", entry.documentFile);
            }

            console.log("Saving tree with payload:", {
                land_owner_id: editFormData.land_owner_id,
                tree_type_name: treeTypeName,
                tree_count: parseInt(entry.count),
                document: entry.documentFile ? entry.documentFile.name : null
            });

            let result;
            if (entry.tree_id && !entry.tree_id.toString().startsWith("saved_")) {
                result = await apiService.patch(
                    `/patchUpdateHighwayTree?treeId=${entry.tree_id}`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
            } else {
                result = await apiService.post("/postAddHighwayTree", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
            }

            if (result.status === 200) {
                const updatedEntries = [...(editFormData.tree_entries || [])];
                updatedEntries[index] = {
                    ...updatedEntries[index],
                    tree_id: result.treeId || result.data?.tree_id || entry.tree_id || `temp_${Date.now()}`,
                    saved: true,
                };
                handleFieldChange("tree_entries", updatedEntries);
                updateTreeFieldFormat(updatedEntries);

                setEditingTreeId(null);
                setError("");
                showAlert("Success", "Tree saved successfully!", "success");
            } else {
                showAlert("Error", result.message || "Failed to save tree", "error");
            }
        } catch (err) {
            console.error("Error saving tree:", err);
            showAlert("Error", "Error saving tree: " + err.message, "error");
        } finally {
            setSaving(false);
        }
    }, [editFormData.land_owner_id, editFormData.tree_entries, treeTypes, handleFieldChange, showAlert]);

    /**
     * Removes tree entry with confirmation
     */
    const removeTreeEntry = useCallback(async (index) => {
        const treeToRemove = editFormData.tree_entries[index];

        if (treeToRemove.saved && treeToRemove.tree_id) {
            const treeTypeName = treeToRemove.other_type ||
                treeTypes.find(t => t.tree_type_id === parseInt(treeToRemove.type_id))?.tree_type_name;

            showConfirm(
                "Delete Tree",
                `Are you sure you want to delete this tree (${treeToRemove.count}x ${treeTypeName})?`,
                async () => {
                    try {
                        const response = await apiService.delete(
                            `/deleteAssetsProperty?type=tree&propertyId=${treeToRemove.tree_id}`
                        );

                        if (response.status === 200) {
                            const updatedEntries = (editFormData.tree_entries || []).filter((_, i) => i !== index);
                            handleFieldChange("tree_entries", updatedEntries);
                            updateTreeFieldFormat(updatedEntries);
                            showAlert("Success", "Tree deleted successfully!", "success");
                        } else {
                            showAlert("Error", "Failed to delete tree", "error");
                        }
                    } catch (error) {
                        console.error("Error deleting tree:", error);
                        showAlert("Error", "Error deleting tree: " + error.message, "error");
                    }
                },
                null,
                "danger"
            );
        } else {
            const updatedEntries = (editFormData.tree_entries || []).filter((_, i) => i !== index);
            handleFieldChange("tree_entries", updatedEntries);
            updateTreeFieldFormat(updatedEntries);
        }
    }, [editFormData.tree_entries, treeTypes, showConfirm, handleFieldChange, showAlert]);

    // ===================================================================
    // HOME MANAGEMENT FUNCTIONS
    // ===================================================================

    /**
     * Adds new home entry
     */
    const addHomeEntry = useCallback(() => {
        const currentHomes = editFormData.home_entries || [];
        const newHome = {
            floors: [{
                type: "ground_floor",
                length: "",
                width: "",
                construction_type: "Permanent House",
                custom_construction_type: "",
            }],
            saved: false,
            document: null
        };
        const newHomes = [...currentHomes, newHome];
        handleFieldChange("home_entries", newHomes);
    }, [editFormData.home_entries, handleFieldChange]);

    /**
     * Updates home entry
     */
    const updateHomeEntry = useCallback((homeIndex, floorIndex, field, value) => {
        const updatedHomes = [...(editFormData.home_entries || [])];
        updatedHomes[homeIndex].floors[floorIndex] = {
            ...updatedHomes[homeIndex].floors[floorIndex],
            [field]: value,
        };
        updatedHomes[homeIndex].saved = false;
        handleFieldChange("home_entries", updatedHomes);
    }, [editFormData.home_entries, handleFieldChange]);

    /**
     * Saves home entry to API with validation
     */
    const saveHome = useCallback(async (homeEntry, homeIndex) => {
        try {
            const floor = homeEntry.floors[0];

            // Validation
            const hasValidFloorInfo = floor.construction_type === "Other" || floor.construction_type === "Tin Shed"
                ? floor.custom_construction_type && floor.custom_construction_type.trim()
                : floor.type;

            if (!floor.length || !floor.width || !hasValidFloorInfo) {
                const missingField = (floor.construction_type === "Other" || floor.construction_type === "Tin Shed")
                    ? "description"
                    : "floor type";
                showAlert(
                    "Validation Error",
                    `Please fill in all home details (${missingField}, length, width)`,
                    "error"
                );
                return;
            }

            if (parseFloat(floor.length) <= 0 || parseFloat(floor.width) <= 0) {
                showAlert("Validation Error", "Length and width must be greater than 0", "error");
                return;
            }

            setSaving(true);

            const formData = new FormData();
            formData.append("land_owner_id", editFormData.land_owner_id);

            // Handle house_floor_no for Tin Shed and Other types
            const houseFloorNo = (floor.construction_type === "Other" || floor.construction_type === "Tin Shed")
                ? floor.custom_construction_type
                : floor.type;

            formData.append("house_floor_no", houseFloorNo);
            formData.append("Property_width", parseFloat(floor.width));
            formData.append("Property_length", parseFloat(floor.length));
            formData.append("total", parseFloat(floor.length) * parseFloat(floor.width));

            // Determine property type based on construction type
            let propertyType = "home";
            if (floor.construction_type === "Tin Shed") {
                propertyType = "tinshed/Godown";
            }
            formData.append("property_type", propertyType);
            formData.append("construction_type", floor.construction_type || "Permanent House");

            // Add document if available
            if (homeEntry.documentFile) {
                formData.append("document", homeEntry.documentFile);
            }

            const propertyId = getPropertyId(homeEntry);
            const result = propertyId
                ? await apiService.patch(`/patchUpdateHighwayProperty?propertyId=${propertyId}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                })
                : await apiService.post("/postAddHighwayProperty", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });

            if (result.status === 200) {
                const updatedHomes = [...(editFormData.home_entries || [])];
                updatedHomes[homeIndex] = {
                    ...updatedHomes[homeIndex],
                    saved: true,
                    property_id: result.data?.property_id || propertyId,
                };
                handleFieldChange("home_entries", updatedHomes);
                updateHomeFieldsAndFormat(updatedHomes);
                await loadHomesFromAPI(editFormData.land_owner_id);
                showAlert("Success", "Home saved successfully!", "success");
            } else {
                showAlert("Error", result.message || "Failed to save home", "error");
            }
        } catch (error) {
            console.error("Error saving home:", error);
            showAlert("Error", "Error saving home: " + error.message, "error");
        } finally {
            setSaving(false);
        }
    }, [editFormData.land_owner_id, editFormData.home_entries, handleFieldChange, showAlert]);

    /**
     * Removes home entry with confirmation
     */
    const removeHomeEntry = useCallback(async (index) => {
        const homeToRemove = editFormData.home_entries[index];
        console.log("Removing home at index:", index);
        console.log("Home to remove:", homeToRemove);

        const propertyId = getPropertyId(homeToRemove);
        console.log("Property ID for deletion:", propertyId);

        if (homeToRemove.saved && propertyId) {
            showConfirm(
                "Delete Home",
                "Are you sure you want to delete this home?",
                async () => {
                    try {
                        const response = await apiService.delete(
                            `/deleteAssetsProperty?type=property&propertyId=${propertyId}`
                        );
                        console.log("Delete API response:", response);

                        if (response.status === 200) {
                            const updatedHomes = (editFormData.home_entries || []).filter((_, i) => i !== index);

                            handleFieldChange("home_entries", updatedHomes);
                            updateHomeFieldsAndFormat(updatedHomes);
                            showAlert("Success", "Home deleted successfully!", "success");
                        } else {
                            showAlert("Error", "Failed to delete home", "error");
                        }
                    } catch (error) {
                        console.error("Error deleting home:", error);
                        showAlert("Error", "Error deleting home: " + error.message, "error");
                    }
                },
                null,
                "danger"
            );
        } else {
            const updatedHomes = (editFormData.home_entries || []).filter((_, i) => i !== index);
            handleFieldChange("home_entries", updatedHomes);
            updateHomeFieldsAndFormat(updatedHomes);
        }
    }, [editFormData.home_entries, showConfirm, handleFieldChange, showAlert]);

    // ===================================================================
    // GENERIC ASSET MANAGEMENT FUNCTIONS
    // ===================================================================

    /**
     * Creates generic asset handlers for different asset types
     */
    const createAssetHandlers = useCallback((assetKey, apiType, validationFn, payloadFn) => ({
        add: () => {
            const entry = { saved: false, document: null };

            // Add specific fields based on asset type
            if (assetKey === 'wirefence_entries') {
                entry.pipes = [{ type: "pipe_1", length: "", height: "" }];
            } else if (assetKey === 'farmpond_entries') {
                entry.dimensions = [{ length: "", width: "", depth: "" }];
            } else if (assetKey === 'well_entries') {
                entry.diameter = ""; entry.depth = "";
            } else if (assetKey === 'borewell_entries') {
                entry.count = ""; entry.depth = "";
            } else if (assetKey === 'pipeline_entries') {
                entry.length = "";
                entry.diameter = "";
            } else if (assetKey === 'other_entries') {
                entry.name = ""; entry.quantity = ""; entry.description = "";
            }

            const updated = [...(editFormData[assetKey] || []), entry];
            handleFieldChange(assetKey, updated);
        },

        update: (index, field, value) => {
            const updated = [...(editFormData[assetKey] || [])];
            if (field.includes('.')) {
                // Handle nested fields like pipes[0].length
                const [parent, child] = field.split('.');
                const parentIndex = parseInt(parent.match(/\d+/)[0]);
                const parentKey = parent.replace(/\[\d+\]/, '');
                updated[index][parentKey][parentIndex][child] = value;
            } else {
                updated[index][field] = value;
            }
            updated[index].saved = false;
            handleFieldChange(assetKey, updated);
        },

        save: async (entry, index) => {
            try {
                // Run validation
                const validationError = validationFn(entry);

                setSaving(true);

                const formData = new FormData();
                formData.append("land_owner_id", editFormData.land_owner_id);
                formData.append("property_type", apiType);

                // Add payload fields
                const payload = payloadFn(entry);
                Object.entries(payload).forEach(([key, value]) => {
                    formData.append(key, value);
                });

                // Add document if available
                if (entry.documentFile) {
                    formData.append("document", entry.documentFile);
                }

                const propertyId = getPropertyId(entry);
                const result = propertyId
                    ? await apiService.patch(`/patchUpdateHighwayProperty?propertyId=${propertyId}`, formData, {
                        headers: { "Content-Type": "multipart/form-data" }
                    })
                    : await apiService.post("/postAddHighwayProperty", formData, {
                        headers: { "Content-Type": "multipart/form-data" }
                    });

                if (result.status === 200) {
                    const updated = [...(editFormData[assetKey] || [])];
                    updated[index] = {
                        ...updated[index],
                        saved: true,
                        property_id: result.data?.property_id || propertyId,
                    };
                    handleFieldChange(assetKey, updated);
                    await refreshAssets(assetKey, editFormData.land_owner_id);
                    showAlert("Success", "Asset saved successfully!", "success");
                }
            } catch (error) {
                showAlert("Error", "Error saving asset: " + error.message, "error");
            } finally {
                setSaving(false);
            }
        },

        remove: async (index) => {
            const currentEntries = editFormData[assetKey] || [];
            const itemToRemove = currentEntries[index];
            const propertyId = getPropertyId(itemToRemove);

            // If not saved in DB yet → just remove locally
            if (!itemToRemove.saved || !propertyId) {
                const updated = currentEntries.filter((_, i) => i !== index);
                handleFieldChange(assetKey, updated);
                return;
            }

            // Otherwise confirm & delete from API + local state
            showConfirm(
                "Delete Asset",
                "Are you sure you want to delete this asset?",
                async () => {
                    try {
                        const response = await apiService.delete(
                            `/deleteAssetsProperty?type=property&propertyId=${propertyId}`
                        );

                        if (response.status === 200) {
                            const updated = currentEntries.filter((_, i) => i !== index);
                            handleFieldChange(assetKey, updated);
                            showAlert("Success", "Asset deleted successfully!", "success");
                        } else {
                            showAlert("Error", "Failed to delete asset", "error");
                        }
                    } catch (error) {
                        showAlert("Error", "Error deleting asset: " + error.message, "error");
                    }
                },
                null,
                "danger"
            );
        }
    }), [editFormData, handleFieldChange, showAlert, showConfirm]);

    // Specific asset handlers
    const wireFenceHandlers = createAssetHandlers(
        'wirefence_entries',
        'wireFencing',
        (entry) => {
            const pipe = entry.pipes?.[0];
            if (!pipe?.length) return "Please fill wire fence length";
            if (parseFloat(pipe.length) <= 0) return "Length must be > 0";
            return null;
        },
        (entry) => ({
            Property_length: parseFloat(entry.pipes[0].length),
            total: parseFloat(entry.pipes[0].length)
        })
    );

    const farmPondHandlers = createAssetHandlers(
        'farmpond_entries',
        'farmPond',
        (entry) => {
            const dim = entry.dimensions?.[0];
            if (!dim?.length || !dim?.width || !dim?.depth) return "Please fill all farm pond dimensions";
            if (parseFloat(dim.length) <= 0 || parseFloat(dim.width) <= 0 || parseFloat(dim.depth) <= 0)
                return "All dimensions must be > 0";
            return null;
        },
        (entry) => {
            const dim = entry.dimensions[0];
            return {
                Property_width: parseFloat(dim.width),
                Property_length: parseFloat(dim.length),
                Property_depth: parseFloat(dim.depth),
                total: parseFloat(dim.length) * parseFloat(dim.width) * parseFloat(dim.depth)
            };
        }
    );

    const wellHandlers = createAssetHandlers(
        'well_entries',
        'well',
        (entry) => {
            if (!entry.diameter || !entry.depth) return "Please fill diameter and depth";
            if (parseFloat(entry.diameter) <= 0 || parseFloat(entry.depth) <= 0) return "Diameter and depth must be > 0";
            return null;
        },
        (entry) => {
            const diameter = parseFloat(entry.diameter) || 0;
            const depth = parseFloat(entry.depth) || 0;
            return {
                Property_length: diameter,
                Property_depth: depth,
                total: diameter * depth
            };
        }
    );

    const borewellHandlers = createAssetHandlers(
        'borewell_entries',
        'borewell',
        (entry) => {
            if (!entry.count || !entry.depth) return "Please fill count and depth";
            if (parseInt(entry.count) <= 0 || parseFloat(entry.depth) <= 0) return "Count and depth must be > 0";
            return null;
        },
        (entry) => ({
            Property_depth: parseFloat(entry.depth),
            total: parseFloat(entry.depth)
        })
    );

    const pipelineHandlers = createAssetHandlers(
        'pipeline_entries',
        'pipeline',
        (entry) => {
            if (!entry.length || !entry.Property_width) return "Please fill pipeline length and diameter";
            if (parseFloat(entry.length) <= 0 || parseFloat(entry.Property_width) <= 0) return "Length and diameter must be > 0";
            return null;
        },
        (entry) => ({
            Property_length: parseFloat(entry.length) || 0,
            Property_width: parseFloat(entry.Property_width) || 0,
            total: (parseFloat(entry.length) || 0) * (parseFloat(entry.Property_width) || 0),
        })
    );

    const otherHandlers = createAssetHandlers(
        'other_entries',
        'other',
        (entry) => {
            if (!entry.name || !entry.quantity) return "Please fill name and quantity";
            return null;
        },
        (entry) => ({
            construction_type: entry.name,
            Property_count: entry.quantity
        })
    );

    // ===================================================================
    // API INTEGRATION FUNCTIONS
    // ===================================================================

    /**
     * Fetches available tree types from API
     */
    const fetchTreeTypes = useCallback(async () => {
        try {
            setLoading(true);
            const data = await apiService.get("/getTreeType");

            if (data.status === 200) {
                const activeTreeTypes = data.data.filter((type) => type.is_active);
                setTreeTypes(activeTreeTypes);

                const categories = [...new Set(activeTreeTypes.map(type => type.tree_type_name1))].filter(Boolean);
                setTreeCategories(categories);

                console.log("Loaded tree types:", activeTreeTypes);
                console.log("Loaded categories:", categories);
            } else {
                setError("Failed to fetch tree types");
                showAlert("Error", "Failed to fetch tree types", "error");
            }
        } catch (err) {
            setError("Error fetching tree types: " + err.message);
            showAlert("Error", "Error fetching tree types: " + err.message, "error");
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    /**
     * Loads existing assets from API with document field mapping
     */
    const loadExistingAssetsFromAPI = useCallback(async () => {
        try {
            const ownerId = editFormData.land_owner_id;
            if (!ownerId) {
                console.log("No owner ID available for loading assets");
                return;
            }

            console.log("Loading existing assets from APIs for ownerId:", ownerId);

            // Load trees from tree API
            try {
                const treeResponse = await apiService.get(`/getHighwayTree?ownerId=${ownerId}`);

                if (treeResponse.status === 200 && treeResponse.data && Array.isArray(treeResponse.data)) {
                    const apiTrees = treeResponse.data.map((treeItem) => {
                        const matchingTreeType = treeTypes.find(
                            (type) => type.tree_type_name === treeItem.tree_type_name
                        );

                        return {
                            count: treeItem.tree_count.toString(),
                            category: matchingTreeType?.tree_type_name1 || "",
                            type_id: matchingTreeType
                                ? matchingTreeType.tree_type_id.toString()
                                : "other",
                            other_type: matchingTreeType ? "" : treeItem.tree_type_name,
                            tree_value_valuer: treeItem.tree_value_valuer || "",
                            tree_value_lao: treeItem.tree_value_lao || "",
                            saved: true,
                            tree_id: treeItem.tree_id,
                            document: treeItem.document || treeItem.Document || null
                        };
                    });

                    handleFieldChange("tree_entries", apiTrees);
                    console.log("Loaded trees from tree API:", apiTrees);
                }
            } catch (treeError) {
                console.error("Error loading trees:", treeError);
            }

            // Load other assets from property API with document mapping
            await Promise.all([
                loadHomesFromAPI(ownerId),
                loadWireFencingFromAPI(ownerId),
                loadFarmPondsFromAPI(ownerId),
                loadWellsFromAPI(ownerId),
                loadBorewellsFromAPI(ownerId),
                loadPipelinesFromAPI(ownerId),
                loadOtherAssetsFromAPI(ownerId),
            ]);

            console.log("All assets loaded successfully");
        } catch (error) {
            console.error("Error loading assets:", error);
            showAlert("Error", "Error loading assets: " + error.message, "error");
        }
    }, [editFormData.land_owner_id, treeTypes, handleFieldChange, showAlert]);

    /**
     * Load functions for different asset types with document field mapping
     */
    const loadHomesFromAPI = useCallback(async (ownerId) => {
        try {
            // Load both home and tinshed/Godown types
            const [homeResponse, tinShedResponse] = await Promise.all([
                apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=home`),
                apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=tinshed/Godown`)
            ]);

            const allHomes = [];

            // Process regular homes
            if (homeResponse.status === 200 && homeResponse.data && homeResponse.data.length > 0) {
                const apiHomes = homeResponse.data.map((home) => {
                    const isOtherType = home.construction_type === "Other";
                    const isTinShed = home.construction_type === "Tin Shed";

                    return {
                        floors: [
                            {
                                type: (isOtherType || isTinShed) ? "" : home.house_floor_no || "ground_floor",
                                custom_construction_type: (isOtherType || isTinShed) ? home.house_floor_no || "" : "",
                                length: home.Property_length?.toString() || "",
                                width: home.Property_width?.toString() || "",
                                construction_type: home.construction_type || "Permanent House",
                            },
                        ],
                        saved: true,
                        property_id: getPropertyId(home),
                        document: home.document || home.Document || null
                    };
                });
                allHomes.push(...apiHomes);
            }

            // Process tin sheds as homes with "Tin Shed" construction type
            if (tinShedResponse.status === 200 && tinShedResponse.data && tinShedResponse.data.length > 0) {
                const tinShedHomes = tinShedResponse.data.map((shed) => ({
                    floors: [
                        {
                            type: "",
                            custom_construction_type: shed.house_floor_no || "",  // ✅ Read from house_floor_no
                            length: shed.Property_length?.toString() || "",
                            width: shed.Property_width?.toString() || "",
                            construction_type: "Tin Shed",
                        },
                    ],
                    saved: true,
                    property_id: getPropertyId(shed),
                    document: shed.document || shed.Document || null
                }));
                allHomes.push(...tinShedHomes);
            }

            if (allHomes.length > 0) {
                handleFieldChange("home_entries", allHomes);
                updateHomeFieldsAndFormat(allHomes);
                console.log("Loaded homes and tin sheds from API:", allHomes);
            }
        } catch (error) {
            console.error("Error loading homes:", error);
        }
    }, [handleFieldChange]);

    const loadWireFencingFromAPI = useCallback(async (ownerId) => {
        try {
            const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=wireFencing`);
            if (response.status === 200 && response.data && response.data.length > 0) {
                const apiWireFences = response.data.map((fence) => ({
                    pipes: [{ type: "pipe_1", length: fence.Property_length?.toString() || "", height: "2" }],
                    saved: true,
                    property_id: getPropertyId(fence),
                    document: fence.document || fence.Document || null
                }));
                handleFieldChange("wirefence_entries", apiWireFences);
                updateWireFenceFieldsAndFormat(apiWireFences);
            }
        } catch (error) {
            console.error("Error loading wire fencing:", error);
        }
    }, [handleFieldChange]);

    const loadFarmPondsFromAPI = useCallback(async (ownerId) => {
        try {
            const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=farmPond`);
            if (response.status === 200 && response.data && response.data.length > 0) {
                const apiFarmPonds = response.data.map((pond) => ({
                    dimensions: [{
                        type: "main_pond",
                        length: pond.Property_length?.toString() || "",
                        width: pond.Property_width?.toString() || "",
                        depth: pond.Property_depth?.toString() || "",
                    }],
                    saved: true,
                    property_id: getPropertyId(pond),
                    document: pond.document || pond.Document || null
                }));
                handleFieldChange("farmpond_entries", apiFarmPonds);
                updateFarmPondFieldsAndFormat(apiFarmPonds);
            }
        } catch (error) {
            console.error("Error loading farm ponds:", error);
        }
    }, [handleFieldChange]);

    const loadWellsFromAPI = useCallback(async (ownerId) => {
        try {
            const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=well`);
            if (response.status === 200 && response.data?.length > 0) {
                const wells = response.data.map((well) => ({
                    diameter: well.Property_length?.toString() || "",
                    depth: well.Property_depth?.toString() || "",
                    saved: true,
                    property_id: getPropertyId(well),
                    document: well.document || well.Document || null
                }));
                handleFieldChange("well_entries", wells);
                updateWellFormat(wells);
            }
        } catch (error) {
            console.error("Error loading wells:", error);
        }
    }, [handleFieldChange]);

    const loadBorewellsFromAPI = useCallback(async (ownerId) => {
        try {
            const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=borewell`);
            if (response.status === 200 && response.data?.length > 0) {
                const borewells = response.data.map((bw) => ({
                    count: bw.total?.toString() || "1",
                    depth: bw.Property_depth?.toString() || "",
                    saved: true,
                    property_id: getPropertyId(bw),
                    document: bw.document || bw.Document || null
                }));
                handleFieldChange("borewell_entries", borewells);
                updateBorewellFormat(borewells);
            }
        } catch (error) {
            console.error("Error loading borewells:", error);
        }
    }, [handleFieldChange]);

    const loadPipelinesFromAPI = useCallback(async (ownerId) => {
        try {
            const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=pipeline`);
            if (response.status === 200 && response.data?.length > 0) {
                const pipelines = response.data.map((pipe) => ({
                    length: pipe.Property_length?.toString() || "",
                    diameter: pipe.Property_width?.toString() || "",
                    saved: true,
                    property_id: getPropertyId(pipe),
                    Property_width: pipe.Property_width || "",
                    document: pipe.document || pipe.Document || null
                }));
                handleFieldChange("pipeline_entries", pipelines);
                updatePipelineFormat(pipelines);
            }
        } catch (error) {
            console.error("Error loading pipelines:", error);
        }
    }, [handleFieldChange]);

    const loadOtherAssetsFromAPI = useCallback(async (ownerId) => {
        try {
            const response = await apiService.get(`/getHighwayProperty?ownerId=${ownerId}&type=other`);
            if (response.status === 200 && response.data?.length > 0) {
                const others = response.data.map((item) => ({
                    name: item.construction_type || "",
                    quantity: item.Property_count?.toString() || "",
                    description: item.description || "",
                    saved: true,
                    property_id: getPropertyId(item),
                    document: item.document || item.Document || null
                }));
                handleFieldChange("other_entries", others);
                updateOtherFormat(others);
            }
        } catch (error) {
            console.error("Error loading other assets:", error);
        }
    }, [handleFieldChange]);

    // ===================================================================
    // FORMAT UPDATE FUNCTIONS
    // ===================================================================

    const updateTreeFieldFormat = useCallback((entries) => {
        if (!entries || entries.length === 0) {
            handleFieldChange("no_of_tree", "0");
            return;
        }

        const validEntries = entries.filter(
            (entry) => entry.count && (entry.type_id || entry.other_type) && parseInt(entry.count) > 0
        );

        const totalTreeCount = validEntries.reduce((sum, entry) => {
            return sum + parseInt(entry.count);
        }, 0);

        handleFieldChange("no_of_tree", totalTreeCount.toString());
    }, [handleFieldChange]);

    const updateHomeFieldsAndFormat = useCallback((homeEntries) => {
        const homeCount = homeEntries?.length || 0;
        handleFieldChange("no_of_home", homeCount.toString());
    }, [handleFieldChange]);

    const updateWireFenceFieldsAndFormat = useCallback((fenceEntries) => {
        const fenceCount = fenceEntries?.length || 0;
        handleFieldChange("no_of_wirefencing", fenceCount.toString());
    }, [handleFieldChange]);

    const updateFarmPondFieldsAndFormat = useCallback((pondEntries) => {
        const pondCount = pondEntries?.length || 0;
        handleFieldChange("no_of_fram_pound", pondCount.toString());
    }, [handleFieldChange]);

    const updateWellFormat = useCallback((entries) => {
        handleFieldChange("no_of_well", entries?.length?.toString() || "0");
    }, [handleFieldChange]);

    const updateBorewellFormat = useCallback((entries) => {
        handleFieldChange("no_of_borewell", entries?.length?.toString() || "0");
    }, [handleFieldChange]);

    const updatePipelineFormat = useCallback((entries) => {
        handleFieldChange("no_of_pipeline", entries?.length?.toString() || "0");
    }, [handleFieldChange]);

    const updateOtherFormat = useCallback((entries) => {
        handleFieldChange("no_of_other_asset", entries?.length?.toString() || "0");
    }, [handleFieldChange]);

    // ===================================================================
    // GPS AND LOCATION SERVICES
    // ===================================================================

    /**
     * Gets current GPS location
     */
    const getCurrentLocation = useCallback(() => {
        setIsGettingLocation(true);

        if (!navigator.geolocation) {
            showAlert("Error", "Geolocation is not supported by this browser.", "error");
            setIsGettingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                handleFieldChange("latitude", latitude.toFixed(8));
                handleFieldChange("longitude", longitude.toFixed(8));
                showAlert(
                    "Success",
                    `Location retrieved successfully! Accuracy: ${position.coords.accuracy.toFixed(0)}m`,
                    "success"
                );
                setIsGettingLocation(false);
            },
            (error) => {
                let errorMessage = "Unable to retrieve your location.";
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "Location access denied. Please enable location permissions.";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information is unavailable.";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "Location request timed out. Please try again.";
                        break;
                }
                showAlert("Error", errorMessage, "error");
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0,
            }
        );
    }, [handleFieldChange, showAlert]);

    /**
     * Opens map view with current coordinates
     */
    const openMapView = useCallback(() => {
        if (editFormData.latitude && editFormData.longitude) {
            const mapUrl = `https://www.google.com/maps?q=${editFormData.latitude},${editFormData.longitude}&z=18`;
            window.open(mapUrl, "_blank");
        } else {
            showAlert("Warning", "Please set coordinates first before viewing on map.", "warning");
        }
    }, [editFormData.latitude, editFormData.longitude, showAlert]);

    // ===================================================================
    // CAMERA FUNCTIONALITY
    // ===================================================================

    /**
     * Opens camera interface for asset photography
     */
    const openCamera = async (assetType, assetIndex, assetData = null) => {
        setCurrentAssetType(assetType);
        setCurrentAssetIndex(assetIndex);
        setCurrentAssetData(assetData);
        setAssetWithCameraOpen(`${assetType}_${assetIndex}`);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                }
            });

            setIsCameraOpen(true);

            await new Promise(resolve => setTimeout(resolve, 0));

            const initializeVideo = async () => {
                if (!videoRef.current) {
                    throw new Error('Video element not available');
                }

                videoRef.current.srcObject = stream;

                return new Promise((resolve, reject) => {
                    const video = videoRef.current;

                    const onLoadedMetadata = () => {
                        video.removeEventListener('loadedmetadata', onLoadedMetadata);
                        video.removeEventListener('error', onError);
                        resolve();
                    };

                    const onError = (error) => {
                        video.removeEventListener('loadedmetadata', onLoadedMetadata);
                        video.removeEventListener('error', onError);
                        reject(new Error('Failed to load video: ' + error.message));
                    };

                    video.addEventListener('loadedmetadata', onLoadedMetadata);
                    video.addEventListener('error', onError);

                    video.load();
                });
            };

            await initializeVideo();

            try {
                await videoRef.current.play();
            } catch (playError) {
                console.warn('Auto-play failed:', playError);
            }

        } catch (error) {
            console.error('Camera initialization error:', error);
            showAlert("Camera Error", "Unable to access camera: " + error.message, "error");

            setIsCameraOpen(false);
            setCurrentAssetType(null);
            setCurrentAssetIndex(null);
            setCurrentAssetData(null);
            setAssetWithCameraOpen(null);
        }
    };

    /**
     * Captures photo with GPS coordinates and metadata
     */
    const capturePhoto = useCallback(async () => {
        if (!videoRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;

        if (video.videoWidth === 0 || video.videoHeight === 0) {
            showAlert("Camera Error", "Video not ready. Please try again.", "error");
            return;
        }

        const ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const safe = (v) => (v ?? "").toString().trim() || "N/A";

        // 🔹 GET PROPOSAL DETAILS FOR BHUSAMPADAN NO.
        const proposalDetails = (() => {
            try {
                const stored = localStorage.getItem("selectedProposal");
                return stored ? JSON.parse(stored) : null;
            } catch (e) {
                console.error("Error parsing selectedProposal:", e);
                return null;
            }
        })();

        const meta = {
            bhusampadan_no: safe(proposalDetails?.section_11_bhusampadan_no), // 🔹 ADD THIS
            surveyNo: safe(landAcquisition?.survey_no || editFormData?.survey_no),
            district: safe(landAcquisition?.dist_name),
            taluka: safe(landAcquisition?.taluka_name),
            village: safe(landAcquisition?.village_name),
            ownerName: safe(editFormData?.actual_occupant || landAcquisition?.landholder_name),
            dateTime: new Date().toLocaleString("en-IN"),
        };

        const drawBanner = (lines) => {
            const padX = Math.round(canvas.width * 0.02);
            const padY = Math.round(canvas.height * 0.02);
            const fontSize = Math.max(14, Math.round(canvas.width * 0.018));
            const lineH = Math.round(fontSize * 1.4);

            ctx.save();
            ctx.font = `${fontSize}px sans-serif`;
            ctx.textBaseline = "top";

            const bannerH = lines.length * lineH + padY * 2;

            ctx.fillStyle = "rgba(0,0,0,0.55)";
            ctx.fillRect(0, canvas.height - bannerH, canvas.width, bannerH);

            ctx.fillStyle = "#fff";
            ctx.shadowColor = "rgba(0,0,0,0.9)";
            ctx.shadowBlur = 6;

            let y = canvas.height - bannerH + padY;
            lines.forEach((line) => {
                ctx.fillText(line, padX, y);
                y += lineH;
            });

            ctx.restore();
        };

        const getPosition = () =>
            new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000,
                });
            });

        try {
            const pos = await getPosition();
            const { latitude, longitude, accuracy } = pos.coords;

            const lines = [
                `Lat: ${latitude.toFixed(6)}  Long: ${longitude.toFixed(6)}  ±${Math.round(accuracy)}m`,
                `Bhusampadan No: ${meta.bhusampadan_no}`, // 🔹 ADD THIS LINE
                `Survey No: ${meta.surveyNo}`,
                `District: ${meta.district}   Taluka: ${meta.taluka}   Village: ${meta.village}`,
                `Landowner: ${meta.ownerName}`,
                `Date/Time: ${meta.dateTime}`,
            ];

            drawBanner(lines);

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    showAlert("Camera Error", "Failed to capture image", "error");
                    return;
                }

                const photoData = {
                    id: Date.now(),
                    blob,
                    url: URL.createObjectURL(blob),
                    latitude,
                    longitude,
                    timestamp: new Date().toISOString(),
                    accuracy,
                    assetType: currentAssetType,
                    assetIndex: currentAssetIndex,
                };

                const photoKey = `${currentAssetType}_${currentAssetIndex}`;
                setCapturedPhotos((prev) => ({ ...prev, [photoKey]: photoData }));

                await uploadPhotoToAPI(photoData, currentAssetData);
                showAlert(
                    "Photo Captured",
                    `Photo captured with GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
                    "success"
                );

                closeCamera();
            }, "image/jpeg", 0.8);
        } catch (err) {
            const lines = [
                `Lat: N/A  Long: N/A`,
                `Bhusampadan No: ${meta.bhusampadan_no}`, // 🔹 ADD THIS LINE
                `Survey No: ${meta.surveyNo}`,
                `District: ${meta.district}   Taluka: ${meta.taluka}   Village: ${meta.village}`,
                `Landowner: ${meta.ownerName}`,
                `Date/Time: ${meta.dateTime}`,
            ];

            drawBanner(lines);

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    showAlert("Camera Error", "Failed to capture image", "error");
                    return;
                }

                const photoData = {
                    id: Date.now(),
                    blob,
                    url: URL.createObjectURL(blob),
                    latitude: null,
                    longitude: null,
                    timestamp: new Date().toISOString(),
                    assetType: currentAssetType,
                    assetIndex: currentAssetIndex,
                };

                const photoKey = `${currentAssetType}_${currentAssetIndex}`;
                setCapturedPhotos((prev) => ({ ...prev, [photoKey]: photoData }));

                await uploadPhotoToAPI(photoData, currentAssetData);
                showAlert("Photo Captured", "Photo captured without GPS coordinates", "warning");
                closeCamera();
            }, "image/jpeg", 0.8);
        }
    }, [currentAssetType, currentAssetIndex, currentAssetData, editFormData, landAcquisition, prefillData, showAlert]);
    
    const uploadPhotoToAPI = useCallback(async (photoData, assetData) => {
        const formData = new FormData();
        formData.append("land_owner_id", editFormData.land_owner_id);
        formData.append("document", photoData.blob, `${photoData.assetType}_${photoData.id}.jpg`);

        try {
            let result;

            switch (photoData.assetType) {
                case 'trees':
                    if (assetData) {
                        const treeType = treeTypes.find(t => t.tree_type_id === parseInt(assetData.type_id));
                        const treeTypeName = assetData.type_id === "other" ? assetData.other_type : (treeType?.tree_type_name || "");

                        formData.append("tree_type_name", treeTypeName);
                        formData.append("tree_count", assetData.count);

                        if (assetData.tree_id && !assetData.tree_id.toString().startsWith("saved_")) {
                            result = await apiService.patch(`/patchUpdateHighwayTree?treeId=${assetData.tree_id}`, formData, {
                                headers: { "Content-Type": "multipart/form-data" }
                            });
                        } else {
                            result = await apiService.post("/postAddHighwayTree", formData, {
                                headers: { "Content-Type": "multipart/form-data" }
                            });
                        }
                    }
                    break;

                default:
                    if (assetData) {
                        const assetTypeMapping = {
                            'homes': 'home',
                            'wirefences': 'wireFencing',
                            'farmponds': 'farmPond',
                            'wells': 'well',
                            'borewells': 'borewell',
                            'pipelines': 'pipeline',
                            'others': 'other'
                        };

                        const apiType = assetTypeMapping[photoData.assetType];
                        if (apiType) {
                            formData.append("property_type", apiType);

                            if (photoData.assetType === 'homes') {
                                const floor = assetData.floors[0];
                                formData.append("house_floor_no", floor.construction_type === "Other" ? floor.custom_construction_type : floor.type);
                                formData.append("Property_width", parseFloat(floor.width));
                                formData.append("Property_length", parseFloat(floor.length));
                                formData.append("total", parseFloat(floor.length) * parseFloat(floor.width));
                                formData.append("construction_type", floor.construction_type || "Permanent House");
                            } else if (photoData.assetType === 'wirefences') {
                                const pipe = assetData.pipes?.[0] || {};
                                formData.append("Property_length", parseFloat(pipe.length));
                                formData.append("Property_width", parseFloat(pipe.height));
                                formData.append("total", parseFloat(pipe.length) * parseFloat(pipe.height));
                            } else if (photoData.assetType === 'farmponds') {
                                const asset = assetData.dimensions?.[0] || {};
                                formData.append("Property_length", parseFloat(asset.length));
                                formData.append("Property_depth", parseFloat(asset.depth));
                                formData.append("Property_width", parseFloat(asset.width));
                                formData.append("total", parseFloat(asset.length) * parseFloat(asset.depth) * parseFloat(asset.width));
                            } else if (photoData.assetType === 'wells') {
                                formData.append("Property_length", parseFloat(assetData.diameter));
                                formData.append("Property_depth", parseFloat(assetData.depth));
                                formData.append("total", parseFloat(assetData.diameter) * parseFloat(assetData.depth));
                            } else if (photoData.assetType === 'borewells') {
                                formData.append("Property_depth", parseFloat(assetData.depth));
                                formData.append("total", parseFloat(assetData.depth));
                            } else if (photoData.assetType === 'pipelines') {
                                const length = parseFloat(assetData.length) || 0;
                                const width = parseFloat(assetData.Property_width || assetData.diameter) || 0;
                                formData.append("Property_length", length);
                                formData.append("Property_width", width);
                                formData.append("total", length * width);
                            } else if (photoData.assetType === 'others') {
                                formData.append("construction_type", assetData.name);
                                formData.append("Property_count", parseFloat(assetData.quantity));
                            }

                            const propertyId = getPropertyId(assetData);
                            if (propertyId) {
                                result = await apiService.patch(`/patchUpdateHighwayProperty?propertyId=${propertyId}`, formData, {
                                    headers: { "Content-Type": "multipart/form-data" }
                                });
                            } else {
                                result = await apiService.post("/postAddHighwayProperty", formData, {
                                    headers: { "Content-Type": "multipart/form-data" }
                                });
                            }
                        }
                    }
                    break;
            }

            if (result && result.status === 200) {
                console.log("Photo uploaded successfully for", photoData.assetType);
            }
        } catch (error) {
            console.error("Error uploading photo to API:", error);
            showAlert("Upload Error", "Failed to upload photo to server: " + error.message, "error");
        }
    }, [editFormData.land_owner_id, treeTypes, showAlert]);

    /**
     * Closes camera and releases resources
     */
    const closeCamera = () => {
        try {
            if (videoRef.current && videoRef.current.srcObject) {
                const tracks = videoRef.current.srcObject.getTracks();
                tracks.forEach(track => {
                    track.stop();
                });
                videoRef.current.srcObject = null;
                videoRef.current.load();
            }
        } catch (error) {
            console.error('Error closing camera:', error);
        } finally {
            setIsCameraOpen(false);
            setCurrentAssetType(null);
            setCurrentAssetIndex(null);
            setCurrentAssetData(null);
            setAssetWithCameraOpen(null);
        }
    };

    // ===================================================================
    // MAIN FORM SUBMISSION HANDLER
    // ===================================================================

    const handleEditOwner = useCallback(async () => {
        try {
            setUpdating(true);

            if (!editFormData?.land_owner_id) {
                showAlert("Error", "Occupant ID not found. Cannot update.", "error");
                return;
            }

            // Update format functions before saving
            if (editFormData.tree_entries) updateTreeFieldFormat(editFormData.tree_entries);
            if (editFormData.home_entries) updateHomeFieldsAndFormat(editFormData.home_entries);
            if (editFormData.wirefence_entries) updateWireFenceFieldsAndFormat(editFormData.wirefence_entries);
            if (editFormData.farmpond_entries) updateFarmPondFieldsAndFormat(editFormData.farmpond_entries);
            if (editFormData.well_entries) updateWellFormat(editFormData.well_entries);
            if (editFormData.borewell_entries) updateBorewellFormat(editFormData.borewell_entries);
            if (editFormData.pipeline_entries) updatePipelineFormat(editFormData.pipeline_entries);
            if (editFormData.other_entries) updateOtherFormat(editFormData.other_entries);
            if (editFormData.nature_of_owner) updateOtherFormat(editFormData.nature_of_owner);

            // Use FormData to handle file uploads
            const formData = new FormData();

            // Add all text fields
            formData.append("project_id", editFormData.project_id);
            formData.append("actual_occupant", editFormData.actual_occupant);
            formData.append("owner_contact", editFormData.owner_contact || "");
            formData.append("owner_email", editFormData.owner_email || "");
            formData.append("nature_of_owner", editFormData.nature_of_owner || "");
            formData.append("owner_address", editFormData.owner_address || "");
            formData.append("aadhaar_number", editFormData.aadhaar_number || "");
            formData.append("pan_number", editFormData.pan_number || "");
            formData.append("no_of_tree", editFormData.no_of_tree || "0");
            formData.append("no_of_home", editFormData.no_of_home || "0");
            formData.append("no_of_water_body", editFormData.no_of_water_body || "0");
            formData.append("no_of_borewell", editFormData.no_of_borewell || "0");
            formData.append("no_of_pipeline", editFormData.no_of_pipeline || "0");
            formData.append("no_of_wirefencing", editFormData.no_of_wirefencing || "0");
            formData.append("no_of_fram_pound", editFormData.no_of_fram_pound || "0");
            formData.append("no_of_other_asset", editFormData.no_of_other_asset || "0");
            formData.append("owner_7_12_irrigable_area", editFormData.owner_7_12_irrigable_area || "0");
            formData.append("owner_7_12_non_irrigable_area", editFormData.owner_7_12_non_irrigable_area || "0");
            formData.append("owner_irrigable_area", editFormData.owner_irrigable_area || "0");
            formData.append("owner_non_irrigable_area", editFormData.owner_non_irrigable_area || "0");
            formData.append("remarks", editFormData.remarks || "");
            formData.append("justification", editFormData.justification || "");
            formData.append("is_active", "1");

            // Add media files if they exist
            if (editFormData.media_video instanceof File) {
                formData.append("media_video", editFormData.media_video);
            }
            if (editFormData.media_image instanceof File) {
                formData.append("media_image", editFormData.media_image);
            }

            console.log("Updating owner with FormData including media files");

            const response = await apiService.patch(
                `/patchUpdateHighwayMeasurement?measureId=${editFormData.land_owner_id}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" }
                }
            );

            if (response?.status === 200) {
                showAlert("Success", "Occupant updated successfully!", "success");
                setEditingRowIndex(null);
                if (onRefresh) await onRefresh();
                if (onClose) onClose();
            } else {
                showAlert("Error", "Update failed. Please try again.", "error");
            }
        } catch (error) {
            console.error("Update error:", error);
            showAlert("Error", "An error occurred while updating. Please try again.", "error");
        } finally {
            setUpdating(false);
        }
    }, [
        editFormData,
        onRefresh,
        onClose,
        showAlert,
        updateTreeFieldFormat,
        updateHomeFieldsAndFormat,
        updateWireFenceFieldsAndFormat,
        updateFarmPondFieldsAndFormat,
        updateWellFormat,
        updateBorewellFormat,
        updatePipelineFormat,
        updateOtherFormat
    ]);

    // ===================================================================
    // UI UTILITY FUNCTIONS
    // ===================================================================

    const toggleSection = useCallback((section) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }, []);

    const renderFieldWithError = (field, children, isValid = false) => {
        const hasError = validationErrors[field];
        const hasValue = editFormData[field];
        const showSuccess = hasValue && !hasError;

        return (
            <div className="space-y-1 relative">
                {children}
                {hasError && (
                    <div className="flex items-center text-red-500 text-xs">
                        <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                        {hasError}
                    </div>
                )}
                {showSuccess && (
                    <div className="flex items-center text-green-500 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                        Valid
                    </div>
                )}
            </div>
        );
    };

    // ===================================================================
    // RENDER HELPER FUNCTIONS
    // ===================================================================

    /**
     * Renders asset entry with universal View button and file upload
     */
    const renderAssetEntry = (entry, index, assetType, config) => {
        const { renderFields, handlers } = config;
        const photoKey = `${assetType}_${index}`;
        const hasPhoto = capturedPhotos[photoKey] || existingPhotos[photoKey];
        const assetKey = `${assetType}_${index}`;
        const isCameraActiveForThisAsset = assetWithCameraOpen === assetKey;
        const isEditing = editingRowIndex === index || editingAssetId === entry.property_id || editingAssetId === entry.tree_id;

        return (
            <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                {renderFields(entry, index, handlers, isEditing)}

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                        <SmallButton
                            variant="primary"
                            onClick={() => openCamera(assetType, index, entry)}
                            disabled={!isEditing && entry.saved || isCameraActiveForThisAsset}
                        >
                            <Camera className="h-4 w-4" />
                            {translations[language].captureimage}
                        </SmallButton>

                        <SmallButton
                            variant="primary"
                            onClick={() => handleViewDocument(entry.document)}
                            disabled={!entry.document}
                            title={entry.document ? "View document" : "No document available"}
                        >
                            <Eye className="h-4 w-4" />
                            {translations[language].Jview}
                        </SmallButton>

                        {entry.saved && (
                            <SmallButton
                                variant={isEditing ? "warning" : "primary"}
                                onClick={() => {
                                    if (isEditing) {
                                        setEditingRowIndex(null);
                                        setEditingAssetId(null);
                                        setEditingAssetType(null);
                                    } else {
                                        setEditingRowIndex(index);
                                        setEditingAssetId(entry.property_id || entry.tree_id);
                                        setEditingAssetType(assetType);
                                    }
                                }}
                            >
                                {isEditing ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" ></Edit>}
                            </SmallButton>
                        )}

                        {hasPhoto && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                <CheckCircle className="h-3 w-3" />
                                Photo
                            </div>
                        )}

                        {entry.document && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                <FileText className="h-3 w-3" />
                                Doc
                            </div>
                        )}

                        {isCameraActiveForThisAsset && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                                <Camera className="h-3 w-3" />
                                Camera Active
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {(!entry.saved || isEditing) && !isCameraActiveForThisAsset && !hasPhoto && (
                            <SmallButton
                                variant="success"
                                onClick={() => {
                                    handlers.save(entry, index);
                                    setEditingRowIndex(null);
                                    setEditingAssetId(null);
                                    setEditingAssetType(null);
                                }}
                                disabled={saving}
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            </SmallButton>
                        )}
                        <SmallButton
                            variant="danger"
                            onClick={() => handlers.remove(index)}
                            disabled={isCameraActiveForThisAsset}
                        >
                            <Trash2 className="h-4 w-4" />
                        </SmallButton>
                    </div>
                </div>

                {entry.saved && !isEditing && !isCameraActiveForThisAsset && (
                    <div className="flex items-center gap-2 text-xs text-green-600 pt-1">
                        <CheckCircle className="h-3 w-3" />
                        Saved to database
                    </div>
                )}

                {isCameraActiveForThisAsset && (
                    <div className="flex items-center gap-2 text-xs text-yellow-600 pt-1">
                        <Camera className="h-3 w-3" />
                        Camera is active - photo will auto-save when captured
                    </div>
                )}
            </div>
        );
    };

    // ===================================================================
    // STEP CONTENT RENDERERS
    // ===================================================================

    const validateEditAreas = () => {
        const errors = [];

        const irrigable_7_12 = parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0;
        const non_irrigable_7_12 = parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0;
        const irrigable_jmr = parseFloat(editFormData.owner_irrigable_area || 0) || 0;
        const non_irrigable_jmr = parseFloat(editFormData.owner_non_irrigable_area || 0) || 0;

        if (irrigable_jmr > irrigable_7_12) {
            errors.push(`JMR Cultivable area (${irrigable_jmr}) cannot exceed 7/12 Cultivable area (${irrigable_7_12})`);
        }

        if (non_irrigable_jmr > non_irrigable_7_12) {
            errors.push(`JMR Non-Cultivable area (${non_irrigable_jmr}) cannot exceed 7/12 Non-Cultivable area (${non_irrigable_7_12})`);
        }

        const total_jmr = irrigable_jmr + non_irrigable_jmr;
        const total_7_12 = irrigable_7_12 + non_irrigable_7_12;

        if (total_jmr > total_7_12) {
            errors.push(`Total JMR area (${total_jmr}) cannot exceed total 7/12 area (${total_7_12})`);
        }

        return errors;
    };

    const renderPropertyOverview = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold">{translations[language].propertyoverview}</h4>
                </div>
            </div>

            <SectionCard>
                <h5 className="text-sm font-medium mb-3">{translations[language].JoccupantInformation}</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {renderFieldWithError(
                        "actual_occupant",
                        <Field label={translations[language].name} error={validationErrors.actual_occupant}>
                            <input
                                type="text"
                                value={editFormData.actual_occupant || ""}
                                onChange={(e) => handleFieldChange("actual_occupant", e.target.value)}
                                className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                placeholder="Enter occupant name"
                            />
                        </Field>
                    )}

                    <Field label={translations[language].number}>
                        <input
                            type="tel"
                            value={editFormData.owner_contact || ""}
                            onChange={(e) => handleFieldChange("owner_contact", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter contact number"
                            maxLength="15"
                        />
                    </Field>

                    <Field label={translations[language].EmailAddress}>
                        <input
                            type="email"
                            value={editFormData.owner_email || ""}
                            onChange={(e) => handleFieldChange("owner_email", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="example@email.com"
                        />
                    </Field>

                    <Field label={translations[language].natureofacquisition}>
                        <input
                            type="text"
                            value={editFormData.nature_of_owner || ""}
                            onChange={(e) => handleFieldChange("nature_of_owner", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter acquisition nature"
                        />
                    </Field>

                    <div className="md:col-span-2">
                        {renderFieldWithError(
                            "owner_address",
                            <Field label={translations[language].Jaddress} error={validationErrors.owner_address}>
                                <textarea
                                    value={editFormData.owner_address || ""}
                                    onChange={(e) => handleFieldChange("owner_address", e.target.value)}
                                    className="w-full h-16 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none resize-none"
                                    placeholder="Enter complete address with postal code"
                                />
                            </Field>
                        )}
                    </div>
                </div>
            </SectionCard>

            <SectionCard>
                <h5 className="text-sm font-medium mb-3">{translations[language].areasatbar}</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label={translations[language].culti}>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editFormData.owner_7_12_irrigable_area || ""}
                            onChange={(e) => handleFieldChange("owner_7_12_irrigable_area", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter cultivable"
                        />
                    </Field>

                    <Field label={translations[language].nonculti}>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editFormData.owner_7_12_non_irrigable_area || ""}
                            onChange={(e) => handleFieldChange("owner_7_12_non_irrigable_area", e.target.value)}
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter non-cultivable"
                        />
                    </Field>

                    <Field label={translations[language].Jtotal}>
                        <input
                            type="number"
                            step="0.01"
                            disabled
                            value={
                                (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                                (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0)
                            }
                            className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed"
                            placeholder="Auto total"
                        />
                    </Field>
                </div>
            </SectionCard>

            <SectionCard>
                <h5 className="text-sm font-medium mb-3">{translations[language].areaJMR}</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label={translations[language].culti}>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editFormData.owner_irrigable_area || ""}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                const total7_12 = (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0);
                                const currentNonCultivable = parseFloat(editFormData.owner_non_irrigable_area || 0) || 0;
                                const newCultivable = parseFloat(newValue || 0) || 0;

                                if (newCultivable + currentNonCultivable <= total7_12) {
                                    handleFieldChange("owner_irrigable_area", newValue);
                                }
                            }}
                            className={`w-full h-9 px-3 text-sm rounded-lg border focus:ring-2 focus:ring-blue-400 focus:outline-none ${(() => {
                                const total7_12 = (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0);
                                const jmrTotal = (parseFloat(editFormData.owner_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_non_irrigable_area || 0) || 0);
                                return jmrTotal > total7_12 ? 'border-red-500 bg-red-50' : 'border-gray-300';
                            })()}`}
                            placeholder="Enter cultivable area"
                        />
                    </Field>

                    <Field label={translations[language].nonculti}>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editFormData.owner_non_irrigable_area || ""}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                const total7_12 = (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0);
                                const currentCultivable = parseFloat(editFormData.owner_irrigable_area || 0) || 0;
                                const newNonCultivable = parseFloat(newValue || 0) || 0;

                                if (currentCultivable + newNonCultivable <= total7_12) {
                                    handleFieldChange("owner_non_irrigable_area", newValue);
                                }
                            }}
                            className={`w-full h-9 px-3 text-sm rounded-lg border focus:ring-2 focus:ring-blue-400 focus:outline-none ${(() => {
                                const total7_12 = (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0);
                                const jmrTotal = (parseFloat(editFormData.owner_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_non_irrigable_area || 0) || 0);
                                return jmrTotal > total7_12 ? 'border-red-500 bg-red-50' : 'border-gray-300';
                            })()}`}
                            placeholder="Enter non-cultivable area"
                        />
                    </Field>

                    <Field label={translations[language].Jtotal}>
                        <input
                            type="number"
                            step="0.01"
                            disabled
                            value={
                                (parseFloat(editFormData.owner_irrigable_area || 0) || 0) +
                                (parseFloat(editFormData.owner_non_irrigable_area || 0) || 0)
                            }
                            className={`w-full h-9 px-3 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed ${(() => {
                                const total7_12 = (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0);
                                const jmrTotal = (parseFloat(editFormData.owner_irrigable_area || 0) || 0) +
                                    (parseFloat(editFormData.owner_non_irrigable_area || 0) || 0);
                                return jmrTotal > total7_12 ? 'bg-red-50 border-red-300' : 'bg-gray-100';
                            })()}`}
                            placeholder="Auto-calculated total"
                        />
                    </Field>
                </div>

                {(() => {
                    const total7_12 = (parseFloat(editFormData.owner_7_12_irrigable_area || 0) || 0) +
                        (parseFloat(editFormData.owner_7_12_non_irrigable_area || 0) || 0);
                    const jmrTotal = (parseFloat(editFormData.owner_irrigable_area || 0) || 0) +
                        (parseFloat(editFormData.owner_non_irrigable_area || 0) || 0);
                    const remaining = total7_12 - jmrTotal;

                    if (jmrTotal > total7_12) {
                        return (
                            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-xs text-red-600">
                                    ⚠️ JMR total ({jmrTotal.toFixed(2)}) exceeds 7/12 total ({total7_12.toFixed(2)}) by {(jmrTotal - total7_12).toFixed(2)}
                                </p>
                            </div>
                        );
                    } else if (jmrTotal < total7_12 && jmrTotal > 0) {
                        return (
                            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-600">
                                    ℹ️ Remaining area to allocate: {remaining.toFixed(2)} (Total 7/12: {total7_12.toFixed(2)}, JMR Total: {jmrTotal.toFixed(2)})
                                </p>
                            </div>
                        );
                    } else if (jmrTotal === total7_12 && total7_12 > 0) {
                        return (
                            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-xs text-green-600">
                                    ✓ JMR total matches 7/12 total perfectly ({total7_12.toFixed(2)})
                                </p>
                            </div>
                        );
                    }
                    return null;
                })()}
            </SectionCard>

            <SectionCard>
                <h5 className="text-sm font-medium mb-3">{translations[language].AssetsSummary}</h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 bg-green-50 rounded cursor-pointer hover:bg-green-100 transition-colors" onClick={() => setCurrentStep(2)}>
                        <div className="flex items-center gap-2">
                            <TreePine className="h-4 w-4 text-green-600" />
                            <span className="text-green-800">{translations[language].Jtree}</span>
                        </div>
                        <div className="font-medium text-green-900">
                            {editFormData.tree_entries?.reduce((sum, entry) => sum + (parseInt(entry.count) || 0), 0) || 0}
                        </div>
                    </div>

                    <div className="p-2 bg-indigo-50 rounded cursor-pointer hover:bg-indigo-100 transition-colors" onClick={() => setCurrentStep(3)}>
                        <div className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-indigo-600" />
                            <span className="text-indigo-800">{translations[language].Jhouse}</span>
                        </div>
                        <div className="font-medium text-indigo-900">{editFormData.home_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-cyan-50 rounded cursor-pointer hover:bg-cyan-100 transition-colors" onClick={() => setCurrentStep(6)}>
                        <div className="flex items-center gap-2">
                            <Droplets className="h-4 w-4 text-cyan-600" />
                            <span className="text-cyan-800">{translations[language].Jwells}</span>
                        </div>
                        <div className="font-medium text-cyan-900">{editFormData.well_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-purple-50 rounded cursor-pointer hover:bg-purple-100 transition-colors" onClick={() => setCurrentStep(4)}>
                        <div className="flex items-center gap-2">
                            <Fence className="h-4 w-4 text-purple-600" />
                            <span className="text-purple-800">{translations[language].Jwire}</span>
                        </div>
                        <div className="font-medium text-purple-900">{editFormData.wirefence_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setCurrentStep(5)}>
                        <div className="flex items-center gap-2">
                            <Waves className="h-4 w-4 text-blue-600" />
                            <span className="text-blue-800">{translations[language].Jfarmpond}</span>
                        </div>
                        <div className="font-medium text-blue-900">{editFormData.farmpond_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setCurrentStep(7)}>
                        <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-blue-600" />
                            <span className="text-blue-800">{translations[language].Jborewells}</span>
                        </div>
                        <div className="font-medium text-blue-900">{editFormData.borewell_entries?.length || 0}</div>
                    </div>

                    <div className="p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setCurrentStep(9)}>
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-gray-600" />
                            <span className="text-gray-800">{translations[language].Jothers}</span>
                        </div>
                        <div className="font-medium text-gray-900">{editFormData.other_entries?.length || 0}</div>
                    </div>
                </div>
            </SectionCard>
        </div>
    );

    const renderTreesManagement = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <TreePine className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold">{translations[language].treemanagement}</h4>
                    </div>
                </div>
                <SmallButton variant="success" onClick={addTreeEntry}>
                    <Plus className="h-4 w-4" />
                    {translations[language].addTree}
                </SmallButton>
            </div>

            <SectionCard>
                {(!editFormData.tree_entries || editFormData.tree_entries.length === 0) ? (
                    <div className="text-center py-8">
                        <TreePine className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 mb-3">{translations[language].notreeadded}</p>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto" ref={treeContainerRef}>
                        {editFormData.tree_entries.map((entry, index) =>
                            renderAssetEntry(entry, index, "trees", {
                                renderFields: (entry, index, handlers, isEditing = false) => {
                                    const isDisabled = entry.saved && !isEditing;
                                    return (
                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                            <Field label={translations[language].count}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={entry.count || ""}
                                                    onChange={(e) => updateTreeEntry(index, "count", e.target.value)}
                                                    disabled={isDisabled}
                                                    className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                                    placeholder="Count"
                                                />
                                            </Field>

                                            <Field label={translations[language].category}>
                                                <select
                                                    value={entry.category || ""}
                                                    onChange={(e) => updateTreeEntry(index, "category", e.target.value)}
                                                    disabled={isDisabled}
                                                    className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                                >
                                                    <option value="">{translations[language].Jselectcategory}</option>
                                                    {treeCategories.map((category) => (
                                                        <option key={category} value={category}>
                                                            {category}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>

                                            <Field label={translations[language].treetype}>
                                                <select
                                                    value={entry.type_id || ""}
                                                    onChange={(e) => updateTreeEntry(index, "type_id", e.target.value)}
                                                    disabled={isDisabled || !entry.category}
                                                    className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-green-500 disabled:bg-gray-100"
                                                >
                                                    <option value="">{translations[language].Jselecttreetype}</option>
                                                    {getTreesByCategory(entry.category).map((type) => (
                                                        <option key={type.tree_type_id} value={type.tree_type_id}>
                                                            {type.tree_type_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Field>
                                        </div>
                                    );
                                },
                                handlers: { save: saveTree, remove: removeTreeEntry }
                            })
                        )}
                    </div>
                )}
            </SectionCard>
        </div>
    );

    const renderHomesManagement = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Home className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold">{translations[language].homesManagement}</h4>
                    </div>
                </div>
                <SmallButton variant="primary" onClick={addHomeEntry}>
                    <Plus className="h-4 w-4" />
                    {translations[language].addhome}
                </SmallButton>
            </div>

            <SectionCard>
                {(!editFormData.home_entries || editFormData.home_entries.length === 0) ? (
                    <div className="text-center py-8">
                        <Home className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 mb-3">{translations[language].nohomeadded}</p>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto" ref={homesContainerRef}>
                        {editFormData.home_entries.map((entry, index) =>
                            renderAssetEntry(entry, index, "homes", {
                                renderFields: (home, index, handlers, isEditing = false) => {
                                    const floor = home.floors?.[0] || {};
                                    const isDisabled = home.saved && !isEditing;

                                    return (
                                        <div className="space-y-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                                <Field label={translations[language].constructionType}>
                                                    <select
                                                        value={floor.construction_type || "Permanent House"}
                                                        onChange={(e) => updateHomeEntry(index, 0, "construction_type", e.target.value)}
                                                        disabled={isDisabled}
                                                        className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                    >
                                                        <option value="Permanent House">Permanent House (RCC)</option>
                                                        {/* <option value="Temporary House">RCC</option> */}
                                                        <option value="Tin Shed">Tin Shed</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </Field>

                                                {/* Show text input for Tin Shed */}
                                                {floor.construction_type === "Tin Shed" ? (
                                                    <Field label="Custom Type *">
                                                        <input
                                                            type="text"
                                                            value={floor.custom_construction_type || ""}
                                                            onChange={(e) => updateHomeEntry(index, 0, "custom_construction_type", e.target.value)}
                                                            disabled={isDisabled}
                                                            className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="Enter tin shed description"
                                                        />
                                                    </Field>
                                                ) : floor.construction_type === "Other" ? (
                                                    <Field label="Custom Type *">
                                                        <input
                                                            type="text"
                                                            value={floor.custom_construction_type || ""}
                                                            onChange={(e) => updateHomeEntry(index, 0, "custom_construction_type", e.target.value)}
                                                            disabled={isDisabled}
                                                            className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                            placeholder="Custom type"
                                                        />
                                                    </Field>
                                                ) : (
                                                    <Field label={translations[language].floortype}>
                                                        <select
                                                            value={floor.type || ""}
                                                            onChange={(e) => updateHomeEntry(index, 0, "type", e.target.value)}
                                                            disabled={isDisabled}
                                                            className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                        >
                                                            <option value="">{translations[language].selectfloor}</option>
                                                            <option value="ground_floor">{translations[language].groundfloor}</option>
                                                            {floor.construction_type === "Permanent House" && (
                                                                <>
                                                                    <option value="first_floor">{translations[language].ffloor}</option>
                                                                    <option value="second_floor">{translations[language].sfloor}</option>
                                                                </>
                                                            )}
                                                        </select>
                                                    </Field>
                                                )}

                                                <Field label={translations[language].length}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={floor.length || ""}
                                                        onChange={(e) => updateHomeEntry(index, 0, "length", e.target.value)}
                                                        disabled={isDisabled}
                                                        className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                        placeholder="Length"
                                                    />
                                                </Field>

                                                <Field label={translations[language].width}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={floor.width || ""}
                                                        onChange={(e) => updateHomeEntry(index, 0, "width", e.target.value)}
                                                        disabled={isDisabled}
                                                        className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                                                        placeholder="Width"
                                                    />
                                                </Field>
                                            </div>

                                            {floor.length && floor.width && (
                                                <div className="text-xs text-indigo-600 bg-indigo-50 p-2 rounded">
                                                    {translations[language].totalarea} : {(parseFloat(floor.length) * parseFloat(floor.width)).toFixed(2)} m²
                                                </div>
                                            )}
                                        </div>
                                    );
                                },
                                handlers: { save: saveHome, remove: removeHomeEntry }
                            })
                        )}
                    </div>
                )}
            </SectionCard>
        </div>
    );

    // Generic asset management renderer
    const renderGenericAssetManagement = (assetKey, title, icon, color, handlers, entries) => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                        {React.createElement(icon, { className: `h-4 w-4 text-${color}-600` })}
                    </div>
                    <div>
                        <h4 className="text-lg font-semibold">{title}</h4>
                    </div>
                </div>
                <SmallButton variant="primary" onClick={handlers.add}>
                    <Plus className="h-4 w-4" />
                    Add {title.split(' ')[0]}
                </SmallButton>
            </div>

            <SectionCard>
                {(!entries || entries.length === 0) ? (
                    <div className="text-center py-8">
                        {React.createElement(icon, { className: "h-8 w-8 text-gray-400 mx-auto mb-2" })}
                        <p className="text-sm text-gray-500 mb-3">No {title.toLowerCase()} added yet</p>
                        <SmallButton variant="primary" onClick={handlers.add}>
                            <Plus className="h-4 w-4" />
                            Add First {title}
                        </SmallButton>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {entries.map((entry, index) => renderAssetEntry(entry, index, assetKey.replace('_entries', 's'), {
                            renderFields: (entry, index, handlers, isDisabled) => renderAssetFields(assetKey, entry, index, handlers, isDisabled),
                            handlers
                        }))}
                    </div>
                )}
            </SectionCard>
        </div>
    );

    // Asset field renderers for different types
    const renderAssetFields = (assetKey, entry, index, handlers, isEditing = false) => {
        const isDisabled = entry.saved && !isEditing;

        switch (assetKey) {
            case 'wirefence_entries':
                const pipe = entry.pipes?.[0] || {};
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pipe.length || ""}
                                onChange={(e) => handlers.update(index, 'pipes[0].length', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Height (m)">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pipe.height || ""}
                                onChange={(e) => handlers.update(index, 'pipes[0].height', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                                placeholder="Height"
                            />
                        </Field>
                        <div className="flex items-end">
                            <Field label="Area (m²)">
                                <div className="h-8 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">
                                    {pipe.length && pipe.height
                                        ? (parseFloat(pipe.length) * parseFloat(pipe.height)).toFixed()
                                        : "0.00"}
                                </div>
                            </Field>
                        </div>
                    </div>
                );

            case 'farmpond_entries':
                const dim = entry.dimensions?.[0] || {};
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={dim.length || ""}
                                onChange={(e) => handlers.update(index, 'dimensions[0].length', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Width (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={dim.width || ""}
                                onChange={(e) => handlers.update(index, 'dimensions[0].width', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Width"
                            />
                        </Field>
                        <Field label="Depth (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={dim.depth || ""}
                                onChange={(e) => handlers.update(index, 'dimensions[0].depth', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Depth"
                            />
                        </Field>
                        <div className="flex items-end">
                            <Field label="Volume (m³)">
                                <div className="h-8 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">
                                    {dim.length && dim.width && dim.depth
                                        ? (parseFloat(dim.length) * parseFloat(dim.width) * parseFloat(dim.depth)).toFixed()
                                        : "0.00"}
                                </div>
                            </Field>
                        </div>
                    </div>
                );

            case 'well_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Field label="Diameter (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.diameter || ""}
                                onChange={(e) => handlers.update(index, 'diameter', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-cyan-500 disabled:bg-gray-100"
                                placeholder="Diameter"
                            />
                        </Field>
                        <Field label="Depth (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.depth || ""}
                                onChange={(e) => handlers.update(index, 'depth', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-cyan-500 disabled:bg-gray-100"
                                placeholder="Depth"
                            />
                        </Field>
                    </div>
                );

            case 'borewell_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Field label="Depth (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.depth || ""}
                                onChange={(e) => handlers.update(index, 'depth', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Depth"
                            />
                        </Field>
                    </div>
                );

            case 'pipeline_entries':
                return (
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Length (m) *">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.length || ""}
                                onChange={(e) => handlers.update(index, 'length', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100"
                                placeholder="Length"
                            />
                        </Field>
                        <Field label="Diameter(inch)*">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={entry.Property_width || ""}
                                onChange={(e) => handlers.update(index, 'Property_width', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100"
                                placeholder="Enter diameter"
                            />
                        </Field>
                    </div>
                );

            case 'other_entries':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Field label="Asset Name *">
                            <input
                                type="text"
                                value={entry.name || ""}
                                onChange={(e) => handlers.update(index, 'name', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-gray-500 disabled:bg-gray-100"
                                placeholder="Asset name"
                            />
                        </Field>
                        <Field label="Custom Type *">
                            <input
                                type="text"
                                step="0.01"
                                min="0"
                                value={entry.quantity || ""}
                                onChange={(e) => handlers.update(index, 'quantity', e.target.value)}
                                disabled={isDisabled}
                                className="w-full h-8 px-2 text-sm rounded border border-gray-300 focus:ring-1 focus:ring-gray-500 disabled:bg-gray-100"
                                placeholder="Description"
                            />
                        </Field>
                    </div>
                );

            default:
                return <div className="text-sm text-gray-600">Asset #{index + 1}</div>;
        }
    };

    const renderLocationAndMedia = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Navigation className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold">Media</h4>
                    <p className="text-sm text-gray-500">Upload and manage property media files</p>
                </div>
            </div>

            <SectionCard>
                <h5 className="text-sm font-medium mb-3">Property Media</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Video Upload */}
                    <Field label="Property Video">
                        <div className="space-y-2">
                            <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                <div className="flex flex-col items-center">
                                    <Video className="h-6 w-6 text-gray-400 mb-1" />
                                    <span className="text-xs text-gray-500">
                                        {selectedVideo ? 'Change Video' : 'Upload Video'}
                                    </span>
                                </div>
                                <input
                                    type="file"
                                    accept=".mp4,.avi,.mov,.wmv,.flv,.webm"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            if (file.size > 100 * 1024 * 1024) {
                                                showAlert("Error", "Video file size should be less than 100MB", "error");
                                                return;
                                            }
                                            setSelectedVideo(file);
                                            setVideoPreview(URL.createObjectURL(file));
                                            handleFieldChange("media_video", file);
                                        }
                                    }}
                                    className="hidden"
                                />
                            </label>

                            {/* Show selected video info - Opens in new tab */}
                            {selectedVideo && (
                                <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                                    <div className="flex items-center gap-2">
                                        <Video className="h-4 w-4 text-green-600" />
                                        <div>
                                            <p className="text-xs font-medium text-green-800">{selectedVideo.name}</p>
                                            <p className="text-xs text-green-600">
                                                {(selectedVideo.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                        </div>
                                    </div>
                                    <SmallButton
                                        variant="primary"
                                        onClick={() => {
                                            if (videoPreview) {
                                                // Open newly uploaded video in new tab
                                                window.open(videoPreview, "_blank");
                                            }
                                        }}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </SmallButton>
                                </div>
                            )}

                            {/* Show existing video from API - Opens in modal */}
                            {!selectedVideo && editFormData.media_video && (
                                <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                                    <div className="flex items-center gap-2">
                                        <Video className="h-4 w-4 text-blue-600" />
                                        <p className="text-xs font-medium text-blue-800">Existing Video</p>
                                    </div>
                                    <SmallButton
                                        variant="primary"
                                        onClick={() => {
                                            // Open existing API video in modal
                                            setDocumentPreview({ isOpen: true, document: editFormData.media_video });
                                        }}
                                    >
                                        <Eye className="h-4 w-4" />
                                        View
                                    </SmallButton>
                                </div>
                            )}
                        </div>
                    </Field>

                    {/* Image Upload */}
                    <Field label="Property Image">
                        <div className="space-y-2">
                            <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                <div className="flex flex-col items-center">
                                    <Upload className="h-6 w-6 text-gray-400 mb-1" />
                                    <span className="text-xs text-gray-500">
                                        {selectedImage ? 'Change Image' : 'Upload Image'}
                                    </span>
                                </div>
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            if (file.size > 10 * 1024 * 1024) {
                                                showAlert("Error", "Image file size should be less than 10MB", "error");
                                                return;
                                            }
                                            setSelectedImage(file);
                                            setImagePreview(URL.createObjectURL(file));
                                            handleFieldChange("media_image", file);
                                        }
                                    }}
                                    className="hidden"
                                />
                            </label>

                            {/* Show selected image info - Opens in new tab */}
                            {selectedImage && (
                                <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                                    <div className="flex items-center gap-2">
                                        <Upload className="h-4 w-4 text-green-600" />
                                        <div>
                                            <p className="text-xs font-medium text-green-800">{selectedImage.name}</p>
                                            <p className="text-xs text-green-600">
                                                {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                        </div>
                                    </div>
                                    <SmallButton
                                        variant="primary"
                                        onClick={() => {
                                            if (imagePreview) {
                                                // Open newly uploaded image in new tab
                                                window.open(imagePreview, "_blank");
                                            }
                                        }}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </SmallButton>
                                </div>
                            )}

                            {/* Show existing image from API - Opens in modal */}
                            {!selectedImage && editFormData.media_image && (
                                <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                                    <div className="flex items-center gap-2">
                                        <Upload className="h-4 w-4 text-blue-600" />
                                        <p className="text-xs font-medium text-blue-800">Existing Image</p>
                                    </div>
                                    <SmallButton
                                        variant="primary"
                                        onClick={() => {
                                            // Open existing API image in modal
                                            setDocumentPreview({ isOpen: true, document: editFormData.media_image });
                                        }}
                                    >
                                        <Eye className="h-4 w-4" />
                                        View
                                    </SmallButton>
                                </div>
                            )}
                        </div>
                    </Field>
                </div>

                {/* Upload Status Message */}
                {(selectedVideo || selectedImage) && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <div className="text-xs text-yellow-800">
                                <p className="font-medium">Files ready to upload</p>
                                <p className="mt-1">Click "Save Changes" at the bottom to upload these media files.</p>
                            </div>
                        </div>
                    </div>
                )}
            </SectionCard>
        </div>
    );

    const renderRemarksManagement = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold">Remarks</h4>
                    <p className="text-sm text-gray-500">Additional remarks and justification</p>
                </div>
            </div>

            <SectionCard>
                <div className="space-y-4">
                    <Field label="Remark">
                        <textarea
                            value={editFormData.justification || ""}
                            onChange={(e) => handleFieldChange("justification", e.target.value)}
                            className="w-full h-24 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:outline-none resize-none"
                            placeholder="Enter any additional remarks about the property or measurement..."
                            rows="4"
                        />
                    </Field>

                    <Field label="Justification">
                        <textarea
                            value={editFormData.remarks || ""}
                            onChange={(e) => handleFieldChange("remarks", e.target.value)}
                            className="w-full h-24 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:outline-none resize-none"
                            placeholder="Enter justification for any special considerations or decisions made..."
                            rows="4"
                        />
                    </Field>
                </div>
            </SectionCard>
        </div>
    );

    const renderCurrentStepContent = () => {
        switch (currentStep) {
            case 1: return renderPropertyOverview();
            case 2: return renderTreesManagement();
            case 3: return renderHomesManagement();
            case 4: return renderGenericAssetManagement('wirefence_entries', 'Wire Fencing', Fence, 'purple', wireFenceHandlers, editFormData.wirefence_entries);
            case 5: return renderGenericAssetManagement('farmpond_entries', 'Farm Ponds', Waves, 'blue', farmPondHandlers, editFormData.farmpond_entries);
            case 6: return renderGenericAssetManagement('well_entries', 'Wells', Droplets, 'cyan', wellHandlers, editFormData.well_entries);
            case 7: return renderGenericAssetManagement('borewell_entries', 'Borewells', Zap, 'blue', borewellHandlers, editFormData.borewell_entries);
            case 8: return renderGenericAssetManagement('pipeline_entries', 'Pipelines', Zap, 'sky', pipelineHandlers, editFormData.pipeline_entries);
            case 9: return renderGenericAssetManagement('other_entries', 'Other Assets', Package, 'gray', otherHandlers, editFormData.other_entries);
            case 10: return renderLocationAndMedia();
            case 11: return renderRemarksManagement();
            default: return <div className="text-center py-8 text-gray-500">Step not found</div>;
        }
    };

    const StepIndicator = ({ step, title, isActive }) => {
        const stepInfo = STEP_DEFINITIONS[step];
        const Icon = stepInfo?.icon || MapPin;

        return (
            <div
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                    }`}
                onClick={() => setCurrentStep(step)}
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isActive ? 'text-blue-800' : 'text-gray-600'}`}>
                        {title}
                    </div>
                </div>
            </div>
        );
    };

    // ===================================================================
    // REACT LIFECYCLE HOOKS
    // ===================================================================

    // Check for mobile screen size
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Initialize arrays
    useEffect(() => {
        const initArrays = {
            tree_entries: [], home_entries: [], wirefence_entries: [],
            farmpond_entries: [], well_entries: [], borewell_entries: [], pipeline_entries: [], other_entries: []
        };
        Object.entries(initArrays).forEach(([key, defaultValue]) => {
            if (!editFormData[key]) {
                handleFieldChange(key, defaultValue);
            }
        });
    }, [editFormData, handleFieldChange]);

    // Fetch tree types on component mount
    useEffect(() => {
        fetchTreeTypes();
    }, [fetchTreeTypes]);

    // Load existing assets when owner ID becomes available
    useEffect(() => {
        if (editFormData.land_owner_id && treeTypes.length > 0) {
            loadExistingAssetsFromAPI();
            loadMediaFiles();
        }
    }, [editFormData.land_owner_id, treeTypes.length, loadExistingAssetsFromAPI]);

    /**
     * Load media files (video and image) from the owner data
     */
    const loadMediaFiles = useCallback(async () => {
        try {
            if (!editFormData.land_owner_id || !landAcquisition) return;

            console.log("Loading media files for owner:", editFormData.land_owner_id);

            // If media fields are already in editFormData, no need to fetch
            if (editFormData.media_video || editFormData.media_image) {
                console.log("Media files already loaded in editFormData");
                return;
            }

            // Get project and proposal IDs from landAcquisition or editFormData
            const projectId = landAcquisition?.project_id || editFormData.project_id;
            const proposalId = landAcquisition?.proposal_id || editFormData.proposal_id;

            if (!projectId || !proposalId) {
                console.log("Missing project or proposal ID, cannot load media files");
                return;
            }

            console.log("Fetching owner data from API with:", { projectId, proposalId });

            // Fetch owner data using getAllOwner endpoint
            const response = await apiService.getHighwayAllOwner(
                landAcquisition?.dist_name,
                landAcquisition?.taluka_name,
                landAcquisition?.village_name,
                projectId,
                proposalId
            );

            console.log("getAllOwner response:", response);

            if (response && response.data && response.data.length > 0) {
                // Find the current owner in the response
                const currentOwner = response.data.find(land =>
                    land.owners?.some(owner => owner.land_owner_id === editFormData.land_owner_id)
                );

                if (currentOwner) {
                    const ownerData = currentOwner.owners.find(
                        owner => owner.land_owner_id === editFormData.land_owner_id
                    );

                    if (ownerData) {
                        console.log("Found owner data with media:", {
                            media_video: ownerData.media_video,
                            media_image: ownerData.media_image
                        });

                        // Update editFormData with media fields
                        if (ownerData.media_video) {
                            handleFieldChange("media_video", ownerData.media_video);
                        }
                        if (ownerData.media_image) {
                            handleFieldChange("media_image", ownerData.media_image);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error loading media files:", error);
        }
    }, [editFormData.land_owner_id, editFormData.project_id, editFormData.proposal_id,
    editFormData.media_video, editFormData.media_image, landAcquisition, handleFieldChange]);

    // ===================================================================
    // MAIN RENDER
    // ===================================================================

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
            <div className={`bg-white rounded-xl shadow-2xl w-full max-h-[95vh] flex flex-col overflow-hidden ${isMobile ? 'max-w-full' : 'max-w-6xl'
                }`}>

                {/* Compact Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {isMobile && (
                                <button
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    className="p-1 hover:bg-white/20 rounded transition-colors"
                                >
                                    <Menu className="h-4 w-4" />
                                </button>
                            )}
                            <div>
                                <h3 className="text-lg font-bold flex items-center">
                                    <Building2 className="h-5 w-5 mr-2" />
                                    {translations[language].editpropertyoccupant}
                                </h3>
                                <p className="text-blue-100 text-xs">
                                    {translations[language].landholderName}  : {owner?.owner_name || 'N/A'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onCancel}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Compact Sidebar */}

                    <div className={`${isMobile
                        ? `fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                        }`
                        : 'w-64 bg-gray-50 border-r'
                        } overflow-y-auto`}>

                        {isMobile && isMobileMenuOpen && (
                            <div className="p-3 border-b flex items-center justify-between">
                                <h4 className="font-medium text-gray-800">Navigation</h4>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        )}

                        <div className="p-3">
                            <h4 className="text-sm font-medium text-gray-800 mb-3">Steps</h4>
                            <div className="space-y-1">
                                {Object.entries(STEP_DEFINITIONS).map(([step, stepInfo]) => {
                                    const stepNumber = parseInt(step);
                                    const isActive = currentStep === stepNumber;
                                    const hasData = hasStepData(stepNumber);
                                    const Icon = stepInfo.icon || MapPin;

                                    return (
                                        <div
                                            key={step}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isActive
                                                ? 'bg-blue-50 border border-blue-200'
                                                : hasData
                                                    ? 'bg-green-400 hover:bg-green-100'
                                                    : 'hover:bg-gray-50'
                                                }`}
                                            onClick={() => setCurrentStep(stepNumber)}
                                        >
                                            <div className="relative">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive
                                                    ? 'bg-blue-100 text-blue-600'
                                                    : hasData
                                                        ? 'bg-green-100 text-green-600'
                                                        : 'bg-gray-100 text-gray-400'
                                                    }`}>
                                                    <Icon className="h-4 w-4" />
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className={`text-sm font-medium truncate ${isActive
                                                    ? 'text-blue-800'
                                                    : hasData
                                                        ? 'text-green-800'
                                                        : 'text-gray-600'
                                                    }`}>
                                                    {stepInfo.title}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Mobile Overlay */}
                    {isMobile && isMobileMenuOpen && (
                        <div
                            className="fixed inset-0 bg-black/50 z-40"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                    )}

                    {/* Main Content */}
                    <div className="flex-1 flex flex-col">
                        {/* Error Display */}
                        {error && (
                            <div className="mx-4 mt-3 p-2 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-r text-sm flex items-center">
                                <AlertCircle className="h-4 w-4 mr-2" />
                                {error}
                            </div>
                        )}

                        {/* Content Area */}
                        <div className="flex-1 overflow-y-auto px-4 py-3">
                            {renderCurrentStepContent()}
                        </div>

                        {/* Compact Footer */}
                        <div className="border-t bg-gray-50 px-4 py-3">
                            <div className={`flex items-center ${isMobile ? 'flex-col-reverse gap-2' : 'justify-between'}`}>
                                <div className="text-xs text-gray-500">
                                    Step {currentStep} of {Object.keys(STEP_DEFINITIONS).length}
                                </div>

                                <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
                                    <SmallButton
                                        variant="secondary"
                                        onClick={onCancel}
                                        disabled={loading}
                                        className={isMobile ? 'flex-1' : ''}
                                    >
                                        {translations[language].Cancel}
                                    </SmallButton>
                                    <SmallButton
                                        variant="success"
                                        onClick={handleEditOwner}
                                        disabled={loading || updating}
                                        className={isMobile ? 'flex-1' : ''}
                                    >
                                        {loading || updating ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                {translations[language].Updating}...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4" />
                                                {translations[language].saveChanges}
                                            </>
                                        )}
                                    </SmallButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Preview Modal */}
            <DocumentPreviewModal
                document={documentPreview.document}
                isOpen={documentPreview.isOpen}
                onClose={() => setDocumentPreview({ isOpen: false, document: null })}
            />

            {/* Camera Modal */}
            {isCameraOpen && (
                <div className="fixed inset-0 z-[60] bg-black bg-opacity-95 flex flex-col">
                    <div className="flex items-center justify-between p-3 text-white">
                        <div>
                            <h2 className="text-lg font-semibold">
                                Camera - {currentAssetType?.charAt(0).toUpperCase() + currentAssetType?.slice(1)} #{currentAssetIndex + 1}
                            </h2>
                            <p className="text-sm opacity-75">Tap capture for photo with GPS</p>
                        </div>
                        <button
                            onClick={closeCamera}
                            className="p-2 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Video Preview */}
                    <div className="flex-1 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-2xl">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full rounded-lg shadow-2xl"
                            />
                            <canvas ref={canvasRef} className="hidden" />

                            {/* GPS Status */}
                            <div className="absolute top-3 left-3 right-3 bg-black/50 text-white p-2 rounded-lg">
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4" />
                                        <span>GPS Ready</span>
                                    </div>
                                    <div className="opacity-75">
                                        {new Date().toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Capture Button */}
                    <div className="flex items-center justify-center p-6">
                        <button
                            onClick={capturePhoto}
                            className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-lg"
                        >
                            <Camera className="h-8 w-8 text-gray-800" />
                        </button>
                    </div>
                </div>
            )}

            {/* Alert Modal */}
            {showAlertModal && (
                <div className={`fixed z-[70] ${isMobile ? 'inset-x-2 top-4' : 'top-4 right-4'}`}>
                    <div className={`bg-white rounded-lg shadow-lg border-l-4 p-3 ${isMobile ? 'w-full' : 'max-w-sm'
                        } ${alertConfig.type === "error" ? "border-red-500" :
                            alertConfig.type === "warning" ? "border-yellow-500" :
                                alertConfig.type === "info" ? "border-blue-500" : "border-green-500"
                        }`}>
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${alertConfig.type === "error" ? "bg-red-100" :
                                    alertConfig.type === "warning" ? "bg-yellow-100" :
                                        alertConfig.type === "info" ? "bg-blue-100" : "bg-green-100"
                                    }`}>
                                    <span className={`text-xs font-bold ${alertConfig.type === "error" ? "text-red-600" :
                                        alertConfig.type === "warning" ? "text-yellow-600" :
                                            alertConfig.type === "info" ? "text-blue-600" : "text-green-600"
                                        }`}>
                                        {alertConfig.type === "error" ? "✕" :
                                            alertConfig.type === "warning" ? "⚠" :
                                                alertConfig.type === "info" ? "ℹ" : "✓"}
                                    </span>
                                </div>
                            </div>
                            <div className="ml-2 flex-1">
                                <h3 className="text-sm font-medium text-gray-900">
                                    {alertConfig.title}
                                </h3>
                                <p className="text-xs text-gray-600 mt-1">
                                    {alertConfig.message}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowAlertModal(false)}
                                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
                    <div className={`bg-white rounded-lg shadow-lg w-full mx-4 ${isMobile ? 'max-w-sm' : 'max-w-sm'}`}>
                        <div className="p-4">
                            <div className={`w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center ${confirmConfig.type === "danger" ? "bg-red-100" :
                                confirmConfig.type === "warning" ? "bg-yellow-100" : "bg-blue-100"
                                }`}>
                                <span className="text-lg">
                                    {confirmConfig.type === "danger" ? "⚠" :
                                        confirmConfig.type === "warning" ? "⚠" : "?"}
                                </span>
                            </div>

                            <h3 className="text-base font-semibold text-gray-900 mb-2 text-center">
                                {confirmConfig.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-4 text-center">
                                {confirmConfig.message}
                            </p>

                            <div className={`flex gap-2 ${isMobile ? 'flex-col-reverse' : ''}`}>
                                <SmallButton
                                    variant="secondary"
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onCancel) confirmConfig.onCancel();
                                    }}
                                    className={isMobile ? 'w-full' : 'flex-1'}
                                >
                                    Cancel
                                </SmallButton>
                                <SmallButton
                                    variant={confirmConfig.type === "danger" ? "danger" : "primary"}
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                                    }}
                                    className={isMobile ? 'w-full' : 'flex-1'}
                                >
                                    Confirm
                                </SmallButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HighwayEditOwner;